// src/pages/Attrition/AttritionDashboard.jsx

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Tag, Spin, Empty, Button, Select, Tooltip, message as antMsg, Modal, Input } from "antd";
import {
  UserDeleteOutlined, ReloadOutlined, UploadOutlined, SyncOutlined,
  RiseOutlined, TeamOutlined, CheckCircleOutlined,
  WarningOutlined, TrophyOutlined, FireOutlined,
} from "@ant-design/icons";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, AreaChart, Area, LineChart, Line,
} from "recharts";
import dayjs from "dayjs";
import api from "../../services/api";

// ── Design tokens ─────────────────────────────────────────────────
const C = {
  navy:"#1B2D6B", blue:"#1E6FBF", lblue:"#EAF1F8",
  green:"#16a34a", lgreen:"#F0FDF4",
  red:"#dc2626",   lred:"#FEF2F2",
  amber:"#b45309", lamber:"#FFFBEB",
  purple:"#7c3aed",lpurple:"#f5f3ff",
  teal:"#0891b2",  lteal:"#ecfeff",
  text1:"#1a2d4a", text2:"#4a6580", text3:"#7a96b0",
  border:"#dce6f0", bg:"#f0f4f8", white:"#ffffff",
};

const PIE_COLORS = ["#dc2626","#1E6FBF","#b45309","#16a34a","#7c3aed",
  "#0891b2","#be185d","#d97706","#4f46e5","#059669"];

const BUCKET_COLORS = {
  "0-10 Days":        "#dc2626",   // deep red   — very early exit
  "10 Days-1 Month":  "#ea580c",   // orange-red — still critical
  "1-2 Months":       "#b45309",   // amber      — early
  "2-3 Months":       "#ca8a04",   // yellow     — early-mid
  "3-4 Months":       "#1E6FBF",   // blue       — mid
  "4-5 Months":       "#0891b2",   // teal       — mid-late
  "5-6 Months":       "#16a34a",   // green      — late
  "6+ Months":        "#7c3aed",   // purple     — long tenure
};

// ── Atoms ─────────────────────────────────────────────────────────
const PctBar = ({pct=0,color=C.blue,h=6,showLabel=true}) => (
  <div style={{display:"flex",alignItems:"center",gap:6}}>
    <div style={{flex:1,height:h,borderRadius:99,background:"#e2e8f0",overflow:"hidden"}}>
      <div style={{width:`${Math.min(pct,100)}%`,height:"100%",
        background:color,borderRadius:99,transition:"width .4s"}}/>
    </div>
    {showLabel&&<span style={{fontSize:11,fontWeight:700,color,minWidth:34}}>{pct}%</span>}
  </div>
);

const Panel = ({children,style={}}) => (
  <div style={{background:C.white,borderRadius:14,padding:"20px 24px",
    border:`1px solid ${C.border}`,
    boxShadow:"0 1px 4px rgba(0,0,0,0.04)",...style}}>
    {children}
  </div>
);

const Sec = ({title,sub,extra}) => (
  <div style={{display:"flex",justifyContent:"space-between",
    alignItems:"flex-end",marginBottom:16}}>
    <div>
      <div style={{fontSize:15,fontWeight:700,color:C.text1}}>{title}</div>
      {sub&&<div style={{fontSize:12,color:C.text3,marginTop:2}}>{sub}</div>}
    </div>
    {extra}
  </div>
);

const ChartTip = ({active,payload,label}) => {
  if(!active||!payload?.length) return null;
  return (
    <div style={{background:C.white,border:`1px solid ${C.border}`,
      borderRadius:10,padding:"10px 14px",
      boxShadow:"0 8px 24px rgba(0,0,0,0.1)",fontSize:12}}>
      <div style={{fontWeight:700,color:C.text1,marginBottom:6}}>{label}</div>
      {payload.map(p=>(
        <div key={p.name} style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
          <span style={{width:8,height:8,borderRadius:2,
            background:p.color,display:"inline-block"}}/>
          <span style={{color:C.text2}}>{p.name}:</span>
          <span style={{fontWeight:700,color:C.text1}}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── KPI Card ─────────────────────────────────────────────────────
const KpiCard = ({icon,label,value,sub,note,color,pct}) => (
  <div style={{
    background:C.white,borderRadius:14,padding:"18px 20px",
    border:`1.5px solid ${color}28`,
    boxShadow:`0 2px 12px ${color}12`,
    display:"flex",flexDirection:"column",gap:8,
  }}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
      <div style={{fontSize:11,fontWeight:700,color:C.text3,
        textTransform:"uppercase",letterSpacing:"0.8px"}}>{label}</div>
      <div style={{width:38,height:38,borderRadius:10,background:`${color}15`,
        display:"flex",alignItems:"center",justifyContent:"center",
        fontSize:18,color,flexShrink:0}}>{icon}</div>
    </div>
    <div style={{fontSize:32,fontWeight:800,color,lineHeight:1,
      letterSpacing:"-1px"}}>{value??0}</div>
    {sub  && <div style={{fontSize:11,color:C.text3}}>{sub}</div>}
    {note && <div style={{fontSize:11,fontWeight:700,color}}>{note}</div>}
    {pct!=null && <PctBar pct={pct} color={color} h={5}/>}
  </div>
);

// ── Summary table with share bars ────────────────────────────────
const SummaryCard = ({title,data,nameLabel="Name",color,total}) => {
  const grand = total || data?.reduce((s,r)=>s+r.count,0) || 1;
  return (
    <div style={{background:C.white,border:`1px solid ${C.border}`,
      borderRadius:14,overflow:"hidden",flex:1,minWidth:260}}>
      <div style={{padding:"12px 16px",fontWeight:700,fontSize:14,color:C.white,
        background:color,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span>{title}</span>
        <span style={{fontSize:12,fontWeight:400,opacity:.8}}>{data?.length||0} entries</span>
      </div>
      <div style={{maxHeight:340,overflowY:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead style={{position:"sticky",top:0,background:C.bg,zIndex:1}}>
            <tr>
              <th style={{padding:"8px 14px",textAlign:"left",fontSize:11,fontWeight:700,
                color:C.text3,textTransform:"uppercase",letterSpacing:"0.5px",
                borderBottom:`1px solid ${C.border}`}}>{nameLabel}</th>
              <th style={{padding:"8px 14px",textAlign:"center",fontSize:11,fontWeight:700,
                color:C.text3,textTransform:"uppercase",letterSpacing:"0.5px",
                borderBottom:`1px solid ${C.border}`,width:60}}>Count</th>
              <th style={{padding:"8px 14px",fontSize:11,fontWeight:700,
                color:C.text3,textTransform:"uppercase",letterSpacing:"0.5px",
                borderBottom:`1px solid ${C.border}`,width:110}}>Share</th>
            </tr>
          </thead>
          <tbody>
            {(data||[]).map((r,i)=>{
              const sharePct = Math.round((r.count/grand)*100);
              return (
                <tr key={i} style={{borderBottom:`1px solid ${C.border}`,
                  background:i%2===0?C.white:"#fafcff"}}>
                  <td style={{padding:"9px 14px",fontSize:13,
                    fontWeight:500,color:C.text1}}>{r.name}</td>
                  <td style={{padding:"9px 14px",textAlign:"center"}}>
                    <span style={{fontWeight:800,fontSize:14,color}}>
                      {r.count}
                    </span>
                  </td>
                  <td style={{padding:"9px 14px"}}>
                    <PctBar pct={sharePct} color={color} h={5}/>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Tenure cross-tab ──────────────────────────────────────────────
const TenureTable = ({title,icon,data,rowKey,buckets,color,monthMode=false,hideHeader=false,
  overrideTotalEmp,overrideTotalResigned}) => {
  if(!data?.length) return null;
  const grandTotalEmp      = overrideTotalEmp      ?? data.reduce((s,r)=>s+(r.totalEmployees||r.total||0),0);
  const grandTotalResigned = overrideTotalResigned ?? data.reduce((s,r)=>s+(r.totalResigned||r.total||0),0);
  // Format month string e.g. "25-Dec" or "Dec 2025" → "Dec, 2025"
  const fmtMonth = (raw) => {
    if (!raw) return "—";
    // Handle "25-Dec" → "Dec, 2025"
    const m1 = raw.match(/^(\d{2})-(\w{3})$/);
    if (m1) { const yr=2000+parseInt(m1[1],10); return `${m1[2]}, ${yr}`; }
    // Handle "Dec 2025" or "Dec, 2025" → "Dec, 2025"
    const m2 = raw.match(/^(\w{3})[,\s]+(\d{4})$/);
    if (m2) return `${m2[1]}, ${m2[2]}`;
    return raw;
  };
  return (
    <div style={{background:C.white,border:hideHeader?'none':`1px solid ${C.border}`,
      borderRadius:hideHeader?0:14,overflow:"hidden"}}>
      {!hideHeader && (
        <div style={{padding:"13px 18px",fontWeight:700,fontSize:14,color:C.white,
          background:`linear-gradient(135deg,${color},${color}cc)`,
          display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span>{icon} {title}</span>
          <span style={{fontSize:12,fontWeight:400,opacity:.8}}>
            {data.length} {rowKey.toLowerCase()}s
            &nbsp;&middot;&nbsp;{grandTotalEmp} employees
            &nbsp;&middot;&nbsp;{grandTotalResigned} resigned
          </span>
        </div>
      )}
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:600}}>
          <thead>
            <tr style={{background:C.navy}}>
              <th style={{padding:"9px 16px",textAlign:"left",fontSize:12,fontWeight:700,
                color:"#fff",minWidth:140,position:"sticky",left:0,background:C.navy,
                borderRight:`1px solid rgba(255,255,255,0.15)`}}>
                {rowKey}
              </th>
              {!monthMode && (
                <th style={{padding:"9px 12px",textAlign:"center",fontSize:11,
                  fontWeight:700,color:"#93c5fd",minWidth:80,
                  borderLeft:`1px solid rgba(255,255,255,0.15)`}}>
                  Total<br/>
                  <span style={{fontSize:10,opacity:.7}}>Employees</span>
                </th>
              )}
              <th style={{padding:"9px 12px",textAlign:"center",fontSize:11,
                fontWeight:700,color:"#fca5a5",minWidth:80,
                borderLeft:`1px solid rgba(255,255,255,0.15)`}}>
                Resigned<br/>
                <span style={{fontSize:10,opacity:.7}}>(Attrition%)</span>
              </th>
              {(buckets||[]).map(b=>(
                <th key={b} colSpan={2} style={{padding:"9px 12px",textAlign:"center",
                  fontSize:11,fontWeight:700,color:BUCKET_COLORS[b]||"#fff",
                  background:C.navy,borderLeft:`1px solid rgba(255,255,255,0.1)`,
                  minWidth:130}}>
                  {b}
                </th>
              ))}
            </tr>
            <tr style={{background:"#eaf1f8"}}>
              <td style={{padding:"5px 16px",fontSize:11,color:C.text3,
                position:"sticky",left:0,background:"#eaf1f8",
                borderRight:`1px solid ${C.border}`}}/>
              {!monthMode && (
                <td style={{padding:"5px 12px",textAlign:"center",
                  fontSize:10,fontWeight:700,color:"#1E6FBF",
                  borderLeft:`1px solid ${C.border}`}}># Total</td>
              )}
              <td style={{padding:"5px 12px",textAlign:"center",
                fontSize:10,fontWeight:700,color:C.red,
                borderLeft:`1px solid ${C.border}`}}># Attri</td>
              {(buckets||[]).map(b=>(
                <React.Fragment key={b}>
                  <td style={{padding:"5px 8px",textAlign:"center",fontSize:10,
                    fontWeight:700,color:BUCKET_COLORS[b],
                    borderLeft:`1px solid ${C.border}`}}># Attri</td>
                  <td style={{padding:"5px 8px",textAlign:"center",fontSize:10,
                    fontWeight:700,color:BUCKET_COLORS[b]}}>CT %</td>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row,i)=>{
              const rawName    = row.division||row.month||row.designation||row.cohort||"—";
              const name       = monthMode ? fmtMonth(rawName) : rawName;
              const totalEmp   = row.totalEmployees||row.total||0;
              const totalResig = row.totalResigned ||row.total||0;
              const attrPct    = totalEmp>0?Math.round((totalResig/totalEmp)*100):0;
              return (
                <tr key={i} style={{borderBottom:`1px solid ${C.border}`,
                  background:i%2===0?C.white:"#fafcff"}}>
                  <td style={{padding:"9px 16px",fontSize:13,fontWeight:700,
                    color:C.text1,position:"sticky",left:0,
                    background:i%2===0?C.white:"#fafcff",
                    borderRight:`1px solid ${C.border}`}}>
                    {name}
                  </td>
                  {!monthMode && (
                    <td style={{padding:"9px 12px",textAlign:"center",
                      borderLeft:`1px solid ${C.border}`}}>
                      <span style={{fontWeight:800,fontSize:14,color:C.blue}}>
                        {totalEmp}
                      </span>
                    </td>
                  )}
                  <td style={{padding:"9px 12px",textAlign:"center",
                    borderLeft:`1px solid ${C.border}`}}>
                    <div style={{display:"flex",flexDirection:"column",
                      alignItems:"center",gap:2}}>
                      <span style={{fontWeight:800,fontSize:14,
                        color:attrPct>30?C.red:attrPct>15?C.amber:C.green}}>
                        {totalResig}
                      </span>
                      {/* {totalResig>0&&(
                        <span style={{fontSize:10,fontWeight:700,
                          color:attrPct>30?C.red:attrPct>15?C.amber:C.green,
                          padding:"1px 5px",borderRadius:10,
                          background:attrPct>30?C.lred:attrPct>15?C.lamber:C.lgreen}}>
                          {attrPct}%
                        </span>
                      )} */}
                    </div>
                  </td>
                  {(buckets||[]).map(b=>{
                    const bData = row.buckets?.[b];
                    const cnt   = typeof bData==="object"?bData.count:(bData||0);
                    const pct   = totalResig?((cnt/totalResig)*100).toFixed(1)+"%":"0%";
                    const bcol  = BUCKET_COLORS[b]||C.blue;
                    return (
                      <React.Fragment key={b}>
                        <td style={{padding:"9px 8px",textAlign:"center",
                          borderLeft:`1px solid ${C.border}`}}>
                          {cnt>0
                            ?<span style={{fontWeight:800,fontSize:14,color:bcol}}>{cnt}</span>
                            :<span style={{color:"#e2e8f0",fontSize:13}}>—</span>}
                        </td>
                        <td style={{padding:"9px 8px",textAlign:"center"}}>
                          {cnt>0
                            ?<span style={{fontSize:11,color:C.text3,padding:"1px 6px",
                                borderRadius:20,background:bcol+"15",fontWeight:600}}>
                                {pct}
                              </span>
                            :<span style={{color:"#e2e8f0"}}>—</span>}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{background:C.navy}}>
              <td style={{padding:"10px 16px",fontWeight:800,fontSize:13,color:"#fff",
                position:"sticky",left:0,background:C.navy}}>TOTAL</td>
              <td style={{padding:"10px 12px",textAlign:"center",fontWeight:800,
                fontSize:14,color:"#93c5fd",
                borderLeft:`1px solid rgba(255,255,255,0.15)`}}>
                {grandTotalEmp}
              </td>
              <td style={{padding:"10px 12px",textAlign:"center",
                borderLeft:`1px solid rgba(255,255,255,0.15)`}}>
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                  <span style={{fontWeight:800,fontSize:14,color:"#fca5a5"}}>
                    {grandTotalResigned}
                  </span>
                  {grandTotalEmp>0&&(
                    <span style={{fontSize:10,color:"rgba(255,255,255,0.6)"}}>
                      {Math.round((grandTotalResigned/grandTotalEmp)*100)}%
                    </span>
                  )}
                </div>
              </td>
              {(buckets||[]).map(b=>{
                const cnt=data.reduce((s,r)=>{
                  const bData=r.buckets?.[b];
                  return s+(typeof bData==="object"?bData.count:(bData||0));
                },0);
                return (
                  <React.Fragment key={b}>
                    <td style={{padding:"10px 8px",textAlign:"center",
                      borderLeft:`1px solid rgba(255,255,255,0.1)`}}>
                      <span style={{fontWeight:800,fontSize:14,
                        color:BUCKET_COLORS[b]||"#fff"}}>{cnt}</span>
                    </td>
                    <td style={{padding:"10px 8px",textAlign:"center"}}>
                      <span style={{fontSize:11,color:"rgba(255,255,255,0.65)"}}>
                        {grandTotalResigned
                          ?((cnt/grandTotalResigned)*100).toFixed(1)+"%"
                          :"0%"}
                      </span>
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
export default function AttritionDashboard() {
  const navigate = useNavigate();
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [barView,    setBarView]    = useState("division");
  const [tenureTab,  setTenureTab]  = useState("month");
  const [chartTab,   setChartTab]   = useState("breakdown"); // "breakdown" | "scorecard"
 // ✅ MOVE HERE (before any return)
  const resignReasonData = React.useMemo(() => {
    const CLOSED  = new Set(['ccd', 'venus']);
    const MAY_MS  = dayjs('2026-05-01').valueOf();
    const DEC_MS  = dayjs('2025-12-01').valueOf();
    const excl = e => {
      const doj = e.dateOfJoining || e.cohortDate;
      const div = (e.division || '').trim().toLowerCase();
      if (CLOSED.has(div) && doj && dayjs(doj).valueOf() > DEC_MS &&
          e.resignedDate && dayjs(e.resignedDate).valueOf() >= MAY_MS) return true;
      if (doj && e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return true;
      if ((e.empCode || '').toUpperCase().startsWith('Y')) return true;
      return false;
    };
    const map = {};
    (stats?.resignedList || []).filter(e => {
      const doj = e.dateOfJoining || e.cohortDate;
      return doj && dayjs(doj).valueOf() > DEC_MS && !excl(e);
    }).forEach(r => {
      let reason = (r.resignedReason || "").trim();
      if (!reason || reason === "—") reason = "Not Provided";
      const key = reason.toLowerCase();
      if (!map[key]) map[key] = { name: reason, value: 0 };
      map[key].value += 1;
    });
    return Object.values(map).sort((a, b) => b.value - a.value).slice(0, 10);
  }, [stats]);
  const [syncLoading,    setSyncLoading]    = useState(false);
  const [showInfoCols,   setShowInfoCols]   = useState(false);
  const [showHistorical, setShowHistorical] = useState(false);
  const [show180Modal,   setShow180Modal]   = useState(false);
  const [modal180Search, setModal180Search] = useState('');
  const [employees,      setEmployees]      = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, empRes] = await Promise.all([
        api.get("/employees/attrition-stats"),
        api.get("/employees", { params: { page: 1, limit: 2000 } }),
      ]);
      const payload = statsRes.data?.data ?? statsRes.data;
      setStats(payload);
      setEmployees(empRes.data?.data || empRes.data || []);
    } catch { setStats(null); }
    finally   { setLoading(false); }
  }, []);

  const handleSharepointSync = async () => {
    setSyncLoading(true);
    try {
      const res = await api.post("/sharepoint-sync/sync-attrition", {}, { timeout: 180000 });
      const r = res.data;
      antMsg.success(
        `Sync done — ${r.inserted} records, ${r.employeesUpdated} employees updated` +
        (r.staleReset ? `, ${r.staleReset} stale flags cleared` : ''),
        6,
      );
      load(); // refresh dashboard with live data
    } catch (e) {
      antMsg.error("SharePoint sync failed: " + (e?.response?.data?.message || e?.message || "Unknown error"));
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{textAlign:"center",paddingTop:100}}>
      <Spin size="large"/>
      <div style={{marginTop:16,color:C.text3,fontSize:13}}>
        Loading attrition analytics…
      </div>
    </div>
  );

  if (!stats) return (
    <div style={{padding:40,textAlign:"center"}}>
      <Empty description="No attrition data available"
        extra={<Button type="primary" onClick={()=>navigate("/attrition/upload")}>
          Upload Attrition File
        </Button>}/>
    </div>
  );

  // ── Derived data ─────────────────────────────────────────────
  const attrPct     = stats.attritionRate||0;
  const retentionPct= Math.round((stats.totalActive/Math.max(stats.total,1))*100);
  const attrColor   = attrPct>30?C.red:attrPct>15?C.amber:C.green;

  // ── Dec'25+ top KPI computations ─────────────────────────────
  const DEC_2025_MS_TOP = dayjs('2025-12-01').valueOf();
  const decPlusJoined = (stats.cohortTenure || []).reduce((sum, ct) => {
    if (!ct.totalEmployees) return sum;
    const rawDate = (ct.cohort?.split(' · ') || [])[1] || '';
    if (!rawDate) return sum;
    const d = dayjs(rawDate, 'DD MMM YY');
    return (d.isValid() && d.valueOf() >= DEC_2025_MS_TOP) ? sum + ct.totalEmployees : sum;
  }, 0);
  const CLOSED_DIVS_TOP  = new Set(['ccd', 'venus']);
  const MAY_2026_MS_TOP  = dayjs('2026-05-01').valueOf();
  const isExcludedTop = e => {
    const doj = e.dateOfJoining || e.cohortDate;
    const div = (e.division || '').trim().toLowerCase();
    if (CLOSED_DIVS_TOP.has(div) && doj && dayjs(doj).valueOf() > DEC_2025_MS_TOP &&
        e.resignedDate && dayjs(e.resignedDate).valueOf() >= MAY_2026_MS_TOP) return true;
    if (doj && e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return true;
    if ((e.empCode || '').toUpperCase().startsWith('Y')) return true;
    return false;
  };
  const decPlusResigned = (stats.resignedList || []).filter(e => {
    const doj = e.dateOfJoining || e.cohortDate;
    return doj && dayjs(doj).valueOf() >= DEC_2025_MS_TOP && !isExcludedTop(e);
  }).length;
  // Active = total enrolled minus tracked-resigned (excluded employees are not counted as resigned)
  const decPlusActive      = decPlusJoined - decPlusResigned;
  const decPlusAttrPct     = decPlusJoined > 0 ? Math.round((decPlusResigned / decPlusJoined) * 100) : 0;
  const decPlusRetentPct   = decPlusJoined > 0 ? Math.round((decPlusActive   / decPlusJoined) * 100) : 100;
  const decAttrColor       = decPlusAttrPct > 30 ? C.red : decPlusAttrPct > 15 ? C.amber : C.green;
  // Historical (Oct'25 + Nov'25) for mini panel
  const histJoinedTop = (stats.cohortTenure || []).reduce((sum, ct) => {
    if (!ct.totalEmployees) return sum;
    const rawDate = (ct.cohort?.split(' · ') || [])[1] || '';
    if (!rawDate) return sum;
    const d = dayjs(rawDate, 'DD MMM YY');
    return (d.isValid() && d.valueOf() < DEC_2025_MS_TOP) ? sum + ct.totalEmployees : sum;
  }, 0);
  const histResignedTop = (stats.resignedList || []).filter(e => {
    const doj = e.dateOfJoining || e.cohortDate;
    return doj && dayjs(doj).valueOf() < DEC_2025_MS_TOP && !isExcludedTop(e);
  }).length;
  const histActiveTop   = histJoinedTop - histResignedTop;
  const histAttrPctTop  = histJoinedTop > 0 ? Math.round((histResignedTop / histJoinedTop) * 100) : 0;

  // Employees who completed the 180-day journey (Dec'25+):
  //   • Active with tenure ≥ 180 days
  //   • Resigned whose tenure AT resignation was ≥ 180 days
  const completed180List = employees.filter(e => {
    const doj = e.dateOfJoining || e.cohortDate;
    if (!doj || dayjs(doj).valueOf() < DEC_2025_MS_TOP) return false;
    if (!e.isResigned) return dayjs().diff(dayjs(doj), 'day') >= 180;
    return e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180;
  });
  // Active still on the 180-day journey (not yet completed, not resigned)
  // decPlusInJourney + completed180 + decPlusResigned = decPlusJoined
  const decPlusInJourney = decPlusActive - completed180List.length;

  // ── Filtered resigned list: Dec'25+ only, After180 / CCD/Venus / Y Code excluded ──
  const filteredResignedList = (stats.resignedList || []).filter(e => {
    const doj = e.dateOfJoining || e.cohortDate;
    return doj && dayjs(doj).valueOf() >= DEC_2025_MS_TOP && !isExcludedTop(e);
  });

  // Recompute monthly counts from filteredResignedList
  const filtResignedByMonth = {};
  filteredResignedList.forEach(e => {
    if (!e.resignedDate) return;
    const m = dayjs(e.resignedDate).format('MMM YYYY');
    filtResignedByMonth[m] = (filtResignedByMonth[m] || 0) + 1;
  });
  const filtByMonth = (stats.byMonth || []).map(m => ({
    ...m,
    count:   filtResignedByMonth[m.name] || 0,
    resigned: filtResignedByMonth[m.name] || 0,
  }));

  // Recompute division-level stats from filteredResignedList
  const filtResignedByDiv = {};
  filteredResignedList.forEach(e => {
    const div = (e.division || '').trim();
    if (div) filtResignedByDiv[div] = (filtResignedByDiv[div] || 0) + 1;
  });
  const filtDivisionRate = (stats.divisionRate || [])
    .filter(d => !CLOSED_DIVS_TOP.has((d.division || '').trim().toLowerCase()))
    .map(d => {
      const res = filtResignedByDiv[d.division] || 0;
      return {
        ...d,
        resigned:      res,
        attritionRate: d.total > 0 ? Math.round((res / d.total) * 10000) / 100 : 0,
      };
    });

  // Resigned employees from the /employees API (has cohortName + cohortDate) — used for cohort-keyed maps
  const decPlusResignedEmps = employees.filter(e => {
    if (!e.isResigned) return false;
    const doj = e.dateOfJoining || e.cohortDate;
    return doj && dayjs(doj).valueOf() >= DEC_2025_MS_TOP && !isExcludedTop(e);
  });

  // Recompute cohort-level stats from decPlusResignedEmps (has cohortName/cohortDate)
  const filtResignedByCohort = {};
  decPlusResignedEmps.forEach(e => {
    const name = (e.cohortName || '').trim();
    const cd   = e.cohortDate;
    if (!name || !cd) return;
    const key = `${name} · ${dayjs(cd).format('DD MMM YY')}`;
    filtResignedByCohort[key] = (filtResignedByCohort[key] || 0) + 1;
  });
  const filtByCohort = (stats.byCohort || []).map(c => {
    const res = filtResignedByCohort[c.name] || 0;
    return { ...c, resigned: res, count: res };
  });

  // ── Filtered Tenure Tables (After180 / CCD / Venus / YCode excluded) ────────
  // Helper: map tenure days → bucket label
  const getTenureBkt = (doj, rd) => {
    if (!doj || !rd) return null;
    const d = dayjs(rd).diff(dayjs(doj), 'day');
    if (d <= 10)  return '0-10 Days';
    if (d <= 30)  return '10 Days-1 Month';
    if (d <= 60)  return '1-2 Months';
    if (d <= 90)  return '2-3 Months';
    if (d <= 120) return '3-4 Months';
    if (d <= 150) return '4-5 Months';
    if (d <= 180) return '5-6 Months';
    return '6+ Months';
  };
  // Build map {groupKey → {totalResigned, buckets}} from filteredResignedList (or custom list)
  const buildFiltTenure = (keyFn, sourceList) => {
    const grouped = {};
    (sourceList || filteredResignedList).forEach(e => {
      const k = keyFn(e); if (!k) return;
      const b = getTenureBkt(e.dateOfJoining, e.resignedDate); if (!b) return;
      if (!grouped[k]) grouped[k] = {};
      grouped[k][b] = (grouped[k][b] || 0) + 1;
    });
    const out = {};
    const bNames = stats.tenureBuckets || [];
    Object.entries(grouped).forEach(([k, bmap]) => {
      const tot = Object.values(bmap).reduce((s, v) => s + v, 0);
      const buckets = {};
      bNames.forEach(b => {
        const c = bmap[b] || 0;
        buckets[b] = { count: c, pct: tot > 0 ? ((c / tot) * 100).toFixed(1) + '%' : '0%' };
      });
      out[k] = { totalResigned: tot, buckets };
    });
    return out;
  };

  // Dec'25+ employee count per division/designation — recomputes totalEmployees from
  // the employees API so backend counts (which include Oct/Nov'25 batch) are replaced
  const decPlusEmpsByDiv = {};
  const decPlusEmpsByDesig = {};
  employees.forEach(e => {
    const doj = e.dateOfJoining || e.cohortDate;
    if (!doj || dayjs(doj).valueOf() < DEC_2025_MS_TOP) return;
    const div  = (e.division    || '').trim();
    const desg = (e.designation || '').trim();
    if (div)  decPlusEmpsByDiv[div]   = (decPlusEmpsByDiv[div]   || 0) + 1;
    if (desg) decPlusEmpsByDesig[desg] = (decPlusEmpsByDesig[desg] || 0) + 1;
  });

  // Division × Tenure — filter CCD/Venus rows + recompute resigned counts + Dec'25+ employee counts
  const filtDivTenureMap = buildFiltTenure(e => (e.division || '').trim() || null);
  const filtDivisionTenure = (stats.divisionTenure || [])
    .filter(d => !CLOSED_DIVS_TOP.has((d.division || '').trim().toLowerCase()))
    .map(d => {
      const k  = (d.division || '').trim();
      const ft = filtDivTenureMap[k] || { totalResigned: 0, buckets: {} };
      return { ...d, totalResigned: ft.totalResigned, buckets: ft.buckets,
               totalEmployees: decPlusEmpsByDiv[k] || 0 };
    });

  // Month × Tenure — recompute resigned counts from filtered list
  const filtMonthTenureMap = buildFiltTenure(e =>
    e.resignedDate ? dayjs(e.resignedDate).format('MMM YYYY') : null
  );
  const filtMonthTenure = (stats.monthTenure || []).map(m => {
    const rawKey = m.month || m.cohort || '';
    let lookupKey = rawKey;
    const m1 = rawKey.match(/^(\d{2})-(\w{3})$/);
    if (m1) lookupKey = dayjs(`20${m1[1]}-${m1[2]}-01`, 'YYYY-MMM-DD').format('MMM YYYY');
    else {
      const m2 = rawKey.match(/^(\w{3})[,\s]+(\d{4})$/);
      if (m2) lookupKey = `${m2[1]} ${m2[2]}`;
    }
    const ft = filtMonthTenureMap[lookupKey] || { totalResigned: 0, buckets: {} };
    return { ...m, totalResigned: ft.totalResigned, buckets: ft.buckets };
  });

  // Designation × Tenure — recompute resigned counts + Dec'25+ employee counts
  const filtDesgTenureMap = buildFiltTenure(e => (e.designation || '').trim() || null);
  const filtDesignationTenure = (stats.designationTenure || []).map(d => {
    const k  = (d.Designation || d.designation || '').trim();
    const ft = filtDesgTenureMap[k] || { totalResigned: 0, buckets: {} };
    return { ...d, totalResigned: ft.totalResigned, buckets: ft.buckets,
             totalEmployees: decPlusEmpsByDesig[k] || 0 };
  });

  // Cohort × Tenure — filter CCD/Venus + pre-Dec'25 cohorts + recompute resigned counts
  const filtCohortTenureMap = buildFiltTenure(e => {
    const name = (e.cohortName || '').trim();
    const cd   = e.cohortDate;
    if (!name || !cd) return null;
    return `${name} · ${dayjs(cd).format('DD MMM YY')}`;
  }, decPlusResignedEmps);
  const filtCohortTenure = (stats.cohortTenure || [])
    .filter(c => {
      const parts = (c.cohort || '').split(' · ');
      // Remove CCD/Venus cohorts
      if (CLOSED_DIVS_TOP.has((parts[0] || '').trim().toLowerCase())) return false;
      // Remove pre-Dec'25 cohorts (Oct/Nov 2025 batch)
      const rawDate = parts[1] || '';
      if (!rawDate) return false;
      const cohortDate = dayjs(rawDate, 'DD MMM YY');
      return cohortDate.isValid() && cohortDate.valueOf() >= DEC_2025_MS_TOP;
    })
    .map(c => {
      const ft = filtCohortTenureMap[c.cohort] || { totalResigned: 0, buckets: {} };
      return { ...c, totalResigned: ft.totalResigned, buckets: ft.buckets };
    });

  // Only include months up to the current month (no future-dated entries in trends)
  const curMonthStart = dayjs().startOf('month').valueOf();
  const pastMonths = filtByMonth.filter(
    m => dayjs(m.name, 'MMM YYYY').startOf('month').valueOf() <= curMonthStart
  );

  // Monthly trend for area chart — last 8 past/current months
  const monthlyTrend = pastMonths
    .slice().reverse().slice(0,8).reverse()
    .map(m=>({ month:m.name, resignations:m.count }));

  const trendSub = monthlyTrend.length > 1
    ? `Resignations per month — ${monthlyTrend[0].month} to ${monthlyTrend[monthlyTrend.length-1].month}`
    : 'Resignations logged per month';

  // Bar chart data — all using filtered (After180/CCD/Venus/YCode excluded) counts
  const barData = {
    division: filtDivisionRate.slice()
      .sort((a,b)=>b.resigned-a.resigned).slice(0,14).map(d=>({
        name:d.division, value:d.attritionRate,
        Joined:d.total, Resigned:d.resigned, Active:d.total-d.resigned,
        rate:d.attritionRate,
      })),
    month: pastMonths.slice()
      .sort((a,b)=>(b.resigned||b.count||0)-(a.resigned||a.count||0))
      .slice(0,12).map(d=>({
        name:d.name,
        Joined:   d.total    || 0,
        Resigned: d.resigned || d.count || 0,
      })),
    cohort: filtByCohort.slice()
      .sort((a,b)=>(b.resigned||b.count||0)-(a.resigned||a.count||0))
      .slice(0,12).map(d=>{
        const parts = d.name.split(" · ");
        return {
          name:       d.name,
          cohortName: parts[0]||d.name,
          cohortDate: parts[1]||"",
          Joined:     d.total    || 0,
          Resigned:   d.resigned || d.count || 0,
        };
      }),
  }[barView]||[];

  const pieData = filtDivisionRate.slice(0,8).map((d,i)=>({
    name:d.division, value:d.resigned,
    fill:PIE_COLORS[i%PIE_COLORS.length],
  }));

  // Top 3 highest attrition divisions (filtered)
  const hotspots = filtDivisionRate
    .filter(d=>d.resigned>0)
    .sort((a,b)=>b.resigned-a.resigned)
    .slice(0,3);

  // Division table columns
  const divCols = [
    { title:"Division", dataIndex:"division", width:130,
      sorter:(a,b)=>a.division.localeCompare(b.division),
      render:v=><span style={{fontWeight:700,color:C.text1}}>{v}</span> },
    { title:"Total",    dataIndex:"total",    width:75, align:"center",
      sorter:(a,b)=>a.total-b.total,
      render:v=><span style={{fontWeight:600,color:C.text2}}>{v}</span> },
    { title:"Active",   width:75, align:"center",
      sorter:(a,b)=>(a.total-a.resigned)-(b.total-b.resigned),
      render:(_,r)=>(
        <span style={{fontWeight:700,color:C.green,
          background:C.lgreen,padding:"2px 8px",borderRadius:20,fontSize:12}}>
          {r.total-r.resigned}
        </span>
      )},
    { title:"Resigned", dataIndex:"resigned", width:85, align:"center",
      sorter:(a,b)=>a.resigned-b.resigned,
      render:v=>v>0
        ?<span style={{fontWeight:700,color:C.red,
            background:C.lred,padding:"2px 8px",borderRadius:20,fontSize:12}}>
            {v}
          </span>
        :<span style={{color:C.text3}}>0</span> },
    { title:"Retention", width:90, align:"center",
      sorter:(a,b)=>(a.total-a.resigned)/a.total-(b.total-b.resigned)/b.total,
      render:(_,r)=>{
        const ret=Math.round(((r.total-r.resigned)/r.total)*100);
        return <span style={{fontWeight:700,
          color:ret>=85?C.green:ret>=70?C.amber:C.red}}>{ret}%</span>;
      }},
    { title:"Attrition Rate", dataIndex:"attritionRate", width:160,
      sorter:(a,b)=>a.attritionRate-b.attritionRate,
      defaultSortOrder:"descend",
      render:v=>{
        const col=v>30?C.red:v>15?C.amber:C.green;
        return (
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{flex:1,height:8,borderRadius:99,
              background:"#e2e8f0",overflow:"hidden"}}>
              <div style={{width:`${Math.min(v,100)}%`,height:"100%",
                borderRadius:99,background:col,transition:"width .4s"}}/>
            </div>
            <span style={{fontSize:12,fontWeight:800,color:col,minWidth:40}}>
              {v}%
            </span>
          </div>
        );
      }},
    { title:"Status", width:90, align:"center",
      render:(_,r)=>{
        const v=r.attritionRate;
        return v>30
          ?<Tag color="error"   style={{borderRadius:20,fontWeight:700}}>🚨 High</Tag>
          :v>15
            ?<Tag color="warning" style={{borderRadius:20,fontWeight:700}}>⚠️ Medium</Tag>
            :<Tag color="success" style={{borderRadius:20,fontWeight:700}}>✅ Low</Tag>;
      }},
  ];

  return (
    <div style={{background:C.bg,minHeight:"100vh"}}>

      {/* ══ HEADER ═══════════════════════════════════════════ */}
      <div style={{
        background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        padding:"22px 28px",marginBottom:24,
        boxShadow:"0 4px 20px rgba(27,45,107,0.3)",
      }}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontSize:22,fontWeight:800,color:"#fff",
              letterSpacing:"-0.5px",display:"flex",alignItems:"center",gap:10}}>
              <UserDeleteOutlined/> Attrition Analytics
            </div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",marginTop:3}}>
              Live resignation analytics · {dayjs().format("DD MMMM YYYY")}
            </div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}
              style={{borderRadius:8,background:"rgba(255,255,255,0.12)",
                border:"1px solid rgba(255,255,255,0.25)",color:"#fff",fontWeight:600}}>
              Refresh
            </Button>
            {histJoinedTop > 0 && (
              <button
                onClick={() => setShowHistorical(v => !v)}
                title="View Oct '25 · Nov '25 historical data"
                style={{
                  padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                  border: `1.5px solid ${showHistorical ? '#fff' : 'rgba(255,255,255,0.45)'}`,
                  background: showHistorical ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.10)',
                  color: '#fff', whiteSpace: 'nowrap',
                }}
              >
                📋 {showHistorical ? 'Hide' : 'Show'} Oct '25 · Nov '25
              </button>
            )}
            <Button
              icon={<SyncOutlined spin={syncLoading}/>}
              onClick={handleSharepointSync}
              loading={syncLoading}
              style={{borderRadius:8,background:"rgba(255,255,255,0.18)",
                border:"1px solid rgba(255,255,255,0.4)",color:"#fff",fontWeight:700}}>
              Sync SharePoint
            </Button>
            <Button icon={<UploadOutlined/>}
              onClick={()=>navigate("/attrition/upload")}
              style={{borderRadius:8,background:"#fff",border:"none",
                color:C.navy,fontWeight:700}}>
              Upload Attrition File
            </Button>
          </div>
        </div>
      </div>

      <div style={{padding:"0 24px 48px"}}>

        {/* ══ HISTORICAL PANEL (Oct'25 · Nov'25) ══════════ */}
        {showHistorical && histJoinedTop > 0 && (
          <div style={{ marginBottom: 20, borderRadius: 12, overflow: 'hidden',
            border: '1.5px dashed #7c3aed' }}>
            <div style={{ background: '#7c3aed', padding: '8px 16px',
              color: '#fff', fontSize: 13, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 8 }}>
              📋 Oct '25 · Nov '25 — Historical Cohort
              <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.8 }}>
                ({histJoinedTop} enrolled)
              </span>
            </div>
            <div style={{ background: '#faf5ff', padding: '12px 16px',
              display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { icon: '👥', label: 'Total Enrolled', value: histJoinedTop,   color: C.navy },
                { icon: '✅', label: 'Active',          value: histActiveTop,   color: C.green,
                  note: `${histJoinedTop > 0 ? Math.round((histActiveTop / histJoinedTop) * 100) : 0}% retention` },
                { icon: '🚪', label: 'Resigned',        value: histResignedTop, color: histResignedTop > 0 ? C.red : C.green,
                  note: `${histAttrPctTop}% attrition` },
                { icon: '🔥', label: 'Attrition %',     value: `${histAttrPctTop}%`, color: histAttrPctTop > 30 ? C.red : histAttrPctTop > 15 ? C.amber : C.green },
              ].map((k, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 8,
                  padding: '8px 14px', border: `1px solid ${C.border}`, minWidth: 120 }}>
                  <div style={{ fontSize: 11, color: C.text3, marginBottom: 2 }}>{k.icon} {k.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.value}</div>
                  {k.note && <div style={{ fontSize: 10, color: C.text3, marginTop: 1 }}>{k.note}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ ROW 1 — KPI CARDS ════════════════════════════ */}
        <div style={{display:"grid",
          gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",
          gap:14,marginBottom:24}}>
          {[
            { icon:<TeamOutlined/>,
              label:"Total Employees",   value:decPlusJoined,
              sub:`${decPlusInJourney} active · ${completed180List.length} completed · ${decPlusResigned} resigned`,
              color:C.navy },
            { icon:<CheckCircleOutlined/>,
              label:"Total Active",  value:decPlusInJourney,
              sub:`${Math.round((decPlusInJourney/Math.max(decPlusJoined,1))*100)}% of total enrolled`,
              note:`✅ Currently on 180-day journey`,
              color:C.green, pct:decPlusRetentPct },
            { icon:<UserDeleteOutlined/>,
              label:"Total Resigned",    value:decPlusResigned,
              sub:"Marked as resigned",
              note:decPlusResigned===0?"🎉 Zero attrition!":null,
              color:decPlusResigned>0?C.red:C.green,
              pct:Math.round((decPlusResigned/Math.max(decPlusJoined,1))*100) },
            { icon:<FireOutlined/>,
              label:"Attrition Index",    value:`${decPlusAttrPct}%`,
              sub:"Overall attrition",
              note:decPlusAttrPct>30?"🚨 Critical — needs attention"
                  :decPlusAttrPct>15?"⚠️ Monitor closely"
                  :"🟢 Within healthy range",
              color:decAttrColor },
            { icon:<RiseOutlined/>,
              label:"Divisions Tracked", value:filtDivisionRate.length||0,
              sub:"Active divisions",
              color:C.purple }
          ].map((k,i)=>(
            <KpiCard key={i} {...k}/>
          ))}
          {/* ── Completed 180-Day Journey — clickable card ─── */}
          <div
            onClick={() => setShow180Modal(true)}
            style={{
              background: C.white, borderRadius: 14, padding: '18px 20px',
              border: '1.5px solid #7c3aed28',
              boxShadow: '0 2px 12px #7c3aed12',
              display: 'flex', flexDirection: 'column', gap: 8,
              cursor: 'pointer', transition: 'transform .15s, box-shadow .15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px #7c3aed22'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 12px #7c3aed12'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.text3,
                textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                Completed 180-Day Journey
              </div>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: '#7c3aed15',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, color: '#7c3aed', flexShrink: 0 }}>
                🎓
              </div>
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#7c3aed',
              lineHeight: 1, letterSpacing: '-1px' }}>
              {completed180List.length}
            </div>
            <div style={{ fontSize: 11, color: C.text3 }}>
              Dec '25 onwards · 180+ days tenure
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>
              👆 Click to view employee list
            </div>
          </div>
        </div>

        {/* ══ ROW 2 — HEALTH BANNER + HOTSPOTS ════════════ */}
        <div style={{display:"grid",
          gridTemplateColumns:"1fr 360px",gap:16,marginBottom:20}}>

          {/* Overall health card */}
          <Panel>
            <Sec title="Workforce Health Overview"
              sub="Active vs resigned breakdown across the organisation"/>
            <div style={{display:"flex",gap:24,alignItems:"center",flexWrap:"wrap"}}>
              {/* Big donut */}
              <div style={{position:"relative",width:140,height:140,flexShrink:0}}>
                <svg width="140" height="140" style={{transform:"rotate(-90deg)"}}>
                  <circle cx="70" cy="70" r="55" fill="none"
                    stroke="#e2e8f0" strokeWidth="16"/>
                  <circle cx="70" cy="70" r="55" fill="none"
                    stroke={C.green} strokeWidth="16"
                    strokeDasharray={`${2*Math.PI*55*decPlusRetentPct/100} ${2*Math.PI*55}`}
                    strokeLinecap="round"/>
                  {decPlusResigned>0&&(
                    <circle cx="70" cy="70" r="55" fill="none"
                      stroke={C.red} strokeWidth="16"
                      strokeDasharray={`${2*Math.PI*55*(100-decPlusRetentPct)/100} ${2*Math.PI*55}`}
                      strokeDashoffset={`-${2*Math.PI*55*decPlusRetentPct/100}`}
                      strokeLinecap="round"/>
                  )}
                </svg>
                <div style={{position:"absolute",inset:0,display:"flex",
                  flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                  <div style={{fontSize:24,fontWeight:800,color:C.green,lineHeight:1}}>
                    {decPlusRetentPct}%
                  </div>
                  <div style={{fontSize:10,fontWeight:700,color:C.text3,
                    textTransform:"uppercase",letterSpacing:"0.5px",marginTop:2}}>
                    Active
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:14}}>
                {[
                  {label:"Active Employees",  val:decPlusActive,
                   pct:decPlusRetentPct,      color:C.green, bg:C.lgreen},
                  {label:"Resigned Employees",val:decPlusResigned,
                   pct:100-decPlusRetentPct,  color:C.red,   bg:C.lred},
                ].map(item=>(
                  <div key={item.label}>
                    <div style={{display:"flex",justifyContent:"space-between",
                      fontSize:13,marginBottom:5}}>
                      <span style={{fontWeight:600,color:C.text1,
                        display:"flex",alignItems:"center",gap:8}}>
                        <span style={{width:10,height:10,borderRadius:2,
                          background:item.color,display:"inline-block"}}/>
                        {item.label}
                      </span>
                      <span style={{fontWeight:800,color:item.color}}>
                        {item.val}
                        <span style={{color:C.text3,fontWeight:400,marginLeft:6,fontSize:12}}>
                          ({item.pct}%)
                        </span>
                      </span>
                    </div>
                    <PctBar pct={item.pct} color={item.color}
                      showLabel={false} h={8}/>
                  </div>
                ))}
                <div style={{padding:"10px 14px",borderRadius:10,
                  background:decPlusAttrPct>30?C.lred:decPlusAttrPct>15?C.lamber:C.lgreen,
                  border:`1px solid ${decAttrColor}33`,
                  fontSize:12,fontWeight:700,color:decAttrColor,marginTop:4}}>
                  {decPlusAttrPct>30
                    ?"🚨 Attrition is critically high — immediate action required"
                    :decPlusAttrPct>15
                      ?"⚠️ Attrition is elevated — monitor and investigate"
                      :decPlusAttrPct>0
                        ?"🟡 Attrition is within acceptable range — continue monitoring"
                        :"🎉 Zero attrition — outstanding retention!"}
                </div>
              </div>
            </div>
          </Panel>

          {/* Hotspot divisions */}
          <Panel>
            <Sec title="🔥 Attrition Hotspots"
              sub="Divisions with highest number of resignations"/>
            {hotspots.length===0 ? (
              <div style={{textAlign:"center",paddingTop:30}}>
                <div style={{fontSize:36,marginBottom:8}}>🎉</div>
                <div style={{fontWeight:700,color:C.green}}>No attrition hotspots!</div>
                <div style={{fontSize:12,color:C.text3,marginTop:4}}>
                  All divisions have healthy retention
                </div>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {hotspots.map((d,i)=>{
                  const maxResigned = hotspots[0].resigned; // highest is first
                  const barPct  = Math.round((d.resigned/Math.max(maxResigned,1))*100);
                  const ratePct = d.attritionRate;
                  const col     = ratePct>30?C.red:ratePct>15?C.amber:C.green;
                  const medals  = ["🥇","🥈","🥉"];
                  return (
                    <div key={d.division} style={{
                      background:col===C.red?C.lred:col===C.amber?C.lamber:C.lgreen,
                      borderRadius:10,padding:"12px 14px",
                      border:`1px solid ${col}33`,
                    }}>
                      <div style={{display:"flex",justifyContent:"space-between",
                        alignItems:"center",marginBottom:6}}>
                        <span style={{fontWeight:700,fontSize:13,color:C.text1}}>
                          {medals[i]} {d.division}
                        </span>
                        {/* Primary: resigned count */}
                        <span style={{fontWeight:800,fontSize:18,color:col}}>
                          {d.resigned}
                          <span style={{fontSize:12,fontWeight:500,
                            color:C.text3,marginLeft:4}}>resigned</span>
                        </span>
                      </div>
                      {/* Bar relative to top hotspot */}
                      <PctBar pct={barPct} color={col} h={6}/>
                      <div style={{display:"flex",gap:14,marginTop:7,
                        fontSize:11,flexWrap:"wrap"}}>
                        <span style={{color:C.text3}}>
                          Total: <strong style={{color:C.text2}}>{d.total}</strong>
                        </span>
                        <span style={{color:C.text3}}>
                          Active: <strong style={{color:C.green}}>{d.total-d.resigned}</strong>
                        </span>
                        <span style={{
                          marginLeft:"auto",fontWeight:700,
                          color:col,
                          background:`${col}15`,
                          padding:"1px 8px",borderRadius:20,
                          border:`1px solid ${col}33`,
                        }}>
                          {ratePct}% attrition
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>

        {/* ══ ROW 3 — TREND + PIE ══════════════════════════ */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 360px",
          gap:16,marginBottom:20}}>

          {/* Monthly resignation trend */}
          <Panel>
            <Sec title="Monthly Resignation Trend" sub={trendSub}/>
            {monthlyTrend.length===0 ? (
              <div style={{textAlign:"center",paddingTop:40,color:C.text3}}>
                No monthly data available
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={190}>
                  <AreaChart data={monthlyTrend}
                    margin={{top:4,right:8,left:-20,bottom:0}}>
                    <defs>
                      <linearGradient id="attGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={C.red} stopOpacity={0.25}/>
                        <stop offset="95%" stopColor={C.red} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3"
                      stroke={C.border} vertical={false}/>
                    <XAxis dataKey="month" tick={{fontSize:11,fill:C.text3}}
                      axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:11,fill:C.text3}}
                      axisLine={false} tickLine={false} allowDecimals={false}/>
                    <RTooltip content={<ChartTip/>}/>
                    <Area type="monotone" dataKey="resignations"
                      name="Resignations" stroke={C.red}
                      fill="url(#attGrad)" strokeWidth={2.5}/>
                  </AreaChart>
                </ResponsiveContainer>
                {/* Month tiles */}
                <div style={{display:"flex",gap:6,marginTop:12,overflowX:"auto"}}>
                  {monthlyTrend.map(m=>(
                    <div key={m.month} style={{
                      flex:"1 1 70px",minWidth:64,textAlign:"center",
                      borderRadius:8,padding:"7px 4px",
                      background:m.resignations>0?C.lred:"#f0fdf4",
                      border:`1px solid ${m.resignations>0?C.red+"44":C.green+"44"}`,
                    }}>
                      <div style={{fontSize:10,color:C.text3,
                        fontWeight:700,marginBottom:2}}>{m.month}</div>
                      <div style={{fontSize:18,fontWeight:800,
                        color:m.resignations>0?C.red:C.green,lineHeight:1}}>
                        {m.resignations}
                      </div>
                      <div style={{fontSize:10,color:C.text3,marginTop:2}}>
                        resigned
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Panel>

          {/* Pie — resignation share */}
          <Panel>
            <Sec title="Resignation Share"
              sub="By division — top 8"/>
            {filteredResignedList.length===0 ? (
              <div style={{textAlign:"center",paddingTop:40}}>
                <div style={{fontSize:48,marginBottom:12}}>🎉</div>
                <div style={{fontWeight:800,fontSize:16,color:C.green}}>
                  Zero Resignations!
                </div>
                <div style={{fontSize:12,color:C.text3,marginTop:6}}>
                  All employees are active and engaged
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name"
                    cx="50%" cy="45%" outerRadius={85}
                    label={({name,percent})=>
                      percent>0.06?`${(percent*100).toFixed(0)}%`:""}
                    labelLine={false}>
                    {pieData.map((e,i)=>(
                      <Cell key={i} fill={e.fill}/>
                    ))}
                  </Pie>
                  <Legend iconType="circle" iconSize={9}
                    formatter={v=>(
                      <span style={{fontSize:11,color:C.text2}}>{v}</span>
                    )}/>
                  <RTooltip contentStyle={{fontSize:12,borderRadius:8,
                    border:`1px solid ${C.border}`}}/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>

        {/* ══ ROWS 4+5 — ATTRITION BREAKDOWN + SCORECARD (tabbed) ═══ */}
        <div style={{marginBottom:20}}>

          {/* Tab bar */}
          <div style={{
            display:"flex",background:C.white,
            borderRadius:"12px 12px 0 0",
            border:`1px solid ${C.border}`,
            borderBottom:"none",overflow:"hidden",
          }}>
            {[
              {key:"breakdown",icon:"📊",label:"Attrition Breakdown",
               sub:"Bar chart — division / month / cohort"},
              {key:"scorecard",icon:"🏢",label:"Division-wise Scorecard",
               sub:"Full scorecard with attrition rates & status"},
            ].map((t,i,arr)=>{
              const active=chartTab===t.key;
              return (
                <button key={t.key} onClick={()=>setChartTab(t.key)} style={{
                  flex:1,padding:"13px 20px",cursor:"pointer",
                  border:"none",
                  borderRight:i<arr.length-1?`1px solid ${C.border}`:"none",
                  borderBottom:active?`3px solid ${C.navy}`:`1px solid ${C.border}`,
                  background:active?C.white:C.surf,
                  textAlign:"left",transition:"all .15s",
                }}>
                  <div style={{fontSize:14,fontWeight:800,
                    color:active?C.navy:C.text2,
                    display:"flex",alignItems:"center",gap:8}}>
                    {t.icon} {t.label}
                  </div>
                  <div style={{fontSize:11,color:C.text3,marginTop:2}}>
                    {t.sub}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div style={{
            background:C.white,
            border:`1px solid ${C.border}`,
            borderTop:"none",
            borderRadius:"0 0 14px 14px",
            overflow:"hidden",
          }}>

            {chartTab==="breakdown" && (
              <div style={{padding:"20px 24px"}}>

          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"flex-end",marginBottom:16}}>
            <div>
              <div style={{fontSize:15,fontWeight:700,color:C.text1}}>
                📊 Attrition Breakdown
              </div>
              <div style={{fontSize:12,color:C.text3,marginTop:2}}>
                {barView==="division"
                  ?"Total Joined vs Resigned per division — side-by-side comparison"
                  :barView==="month"
                    ?"Total joined vs resigned per month — side-by-side comparison"
                    :"Total enrolled vs resigned per cohort — side-by-side comparison"}
              </div>
            </div>
            <Select size="small" value={barView} onChange={setBarView}
              style={{width:140}} options={[
                {value:"division",label:"By Division"},
                {value:"month",   label:"By Month"},
                {value:"cohort",  label:"By Cohort"},
              ]}/>
          </div>

          {/* ── Division: grouped double bar (Total Joined + Resigned) ── */}
          {barView==="division" && (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={filtDivisionRate.slice().sort((a,b)=>b.total-a.total).slice(0,14).map(d=>({
                    name:     d.division,
                    Joined:   d.total,
                    Resigned: d.resigned,
                    Active:   d.total - d.resigned,
                    rate:     d.attritionRate,
                  }))}
                  margin={{top:4,right:8,left:-12,bottom:60}}
                  barCategoryGap="25%"
                  barGap={2}>
                  <CartesianGrid strokeDasharray="3 3"
                    stroke={C.border} vertical={false}/>
                  <XAxis dataKey="name"
                    tick={({x,y,payload})=>(
                      <g transform={`translate(${x},${y})`}>
                        <text x={0} y={0} dy={10} textAnchor="end"
                          fill={C.text2} fontSize={11} fontWeight={600}
                          transform="rotate(-35)">
                          {payload.value?.length>14
                            ?payload.value.slice(0,14)+"…"
                            :payload.value}
                        </text>
                      </g>
                    )}
                    interval={0} axisLine={false} tickLine={false} height={55}/>
                  <YAxis tick={{fontSize:11,fill:C.text3}}
                    axisLine={false} tickLine={false}/>
                  <RTooltip
                    content={({active,payload,label})=>{
                      if(!active||!payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div style={{background:C.white,border:`1px solid ${C.border}`,
                          borderRadius:10,padding:"12px 16px",
                          boxShadow:"0 8px 24px rgba(0,0,0,0.1)",fontSize:12}}>
                          <div style={{fontWeight:700,color:C.text1,
                            marginBottom:8,fontSize:13}}>{label}</div>
                          <div style={{display:"flex",flexDirection:"column",gap:5}}>
                            <div style={{display:"flex",justifyContent:"space-between",
                              gap:20}}>
                              <span style={{color:C.blue}}>🏢 Total Joined</span>
                              <strong style={{color:C.blue}}>{d?.Joined}</strong>
                            </div>
                            <div style={{display:"flex",justifyContent:"space-between",gap:20}}>
                              <span style={{color:C.green}}>✅ Active</span>
                              <strong style={{color:C.green}}>{d?.Active}</strong>
                            </div>
                            <div style={{display:"flex",justifyContent:"space-between",gap:20}}>
                              <span style={{color:C.red}}>🚪 Resigned</span>
                              <strong style={{color:C.red}}>{d?.Resigned}</strong>
                            </div>
                            <div style={{
                              marginTop:4,paddingTop:6,borderTop:`1px solid ${C.border}`,
                              display:"flex",justifyContent:"space-between",gap:20,
                            }}>
                              <span style={{color:C.text3}}>Attrition Rate</span>
                              <strong style={{
                                color:d?.rate>30?C.red:d?.rate>15?C.amber:C.green,
                              }}>{d?.rate}%</strong>
                            </div>
                          </div>
                        </div>
                      );
                    }}/>
                  <Legend
                    wrapperStyle={{fontSize:12,paddingTop:8}}
                    formatter={(value)=>(
                      <span style={{
                        color: value==="Joined"?C.blue
                          :value==="Resigned"?C.red:C.text2,
                        fontWeight:600,
                      }}>{value}</span>
                    )}/>
                  <Bar dataKey="Joined"   name="Joined"
                    fill={C.blue} radius={[4,4,0,0]} maxBarSize={28}
                    fillOpacity={0.85}/>
                  <Bar dataKey="Resigned" name="Resigned"
                    fill={C.red}  radius={[4,4,0,0]} maxBarSize={28}/>
                </BarChart>
              </ResponsiveContainer>

              {/* Risk legend */}
              <div style={{display:"flex",gap:16,marginTop:8,
                padding:"10px 14px",background:C.bg,borderRadius:8,
                border:`1px solid ${C.border}`,flexWrap:"wrap",
                alignItems:"center"}}>
                <span style={{fontSize:11,fontWeight:700,color:C.text3}}>
                  🔵 Blue = Total Joined &nbsp;·&nbsp; 🔴 Red = Resigned
                </span>
                <span style={{marginLeft:"auto",display:"flex",gap:14,flexWrap:"wrap"}}>
                  {[
                    {label:"🚨 High Risk",   desc:">30%",   color:C.red},
                    {label:"⚠️ Medium Risk", desc:"15–30%", color:C.amber},
                    {label:"✅ Low Risk",    desc:"<15%",   color:C.green},
                  ].map(l=>(
                    <div key={l.label} style={{display:"flex",
                      alignItems:"center",gap:5,fontSize:11}}>
                      <span style={{width:8,height:8,borderRadius:2,
                        background:l.color,display:"inline-block"}}/>
                      <span style={{color:l.color,fontWeight:700}}>{l.label}</span>
                      <span style={{color:C.text3}}>({l.desc})</span>
                    </div>
                  ))}
                </span>
              </div>
            </>
          )}

          {/* ── Month / Cohort: grouped double bar ─────────── */}
          {barView!=="division" && (()=>{
            const isCohort = barView==="cohort";

            const CohortTick = ({x,y,payload})=>{
              const item=barData.find(d=>d.name===payload.value)||{};
              const nm=item.cohortName||payload.value;
              const dt=item.cohortDate||"";
              return (
                <g transform={`translate(${x},${y})`}>
                  <text x={0} y={0} dy={10} textAnchor="middle"
                    fill={C.text2} fontSize={11} fontWeight={600}>
                    {nm.length>12?nm.slice(0,12)+"…":nm}
                  </text>
                  {dt&&(
                    <text x={0} y={0} dy={23} textAnchor="middle"
                      fill={C.text3} fontSize={10}>{dt}</text>
                  )}
                </g>
              );
            };
            const defaultTick=({x,y,payload})=>(
              <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={10} textAnchor="end"
                  fill={C.text2} fontSize={10} transform="rotate(-35)">
                  {payload.value?.length>14
                    ?payload.value.slice(0,14)+"…"
                    :payload.value}
                </text>
              </g>
            );
            return (
              <>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={barData}
                    margin={{top:4,right:8,left:-12,
                      bottom:isCohort?56:60}}
                    barCategoryGap="25%" barGap={2}>
                    <CartesianGrid strokeDasharray="3 3"
                      stroke={C.border} vertical={false}/>
                    <XAxis dataKey="name"
                      tick={isCohort?<CohortTick/>:defaultTick}
                      interval={0} axisLine={false} tickLine={false}
                      height={isCohort?56:50}/>
                    <YAxis tick={{fontSize:11,fill:C.text3}}
                      axisLine={false} tickLine={false}/>
                    <RTooltip
                      content={({active,payload,label})=>{
                        if(!active||!payload?.length) return null;
                        const d=payload[0]?.payload;
                        const rate = d?.Joined>0
                          ? Math.round((d.Resigned/d.Joined)*100) : 0;
                        return (
                          <div style={{background:C.white,
                            border:`1px solid ${C.border}`,
                            borderRadius:10,padding:"12px 16px",
                            boxShadow:"0 8px 24px rgba(0,0,0,0.1)",
                            fontSize:12}}>
                            <div style={{fontWeight:700,color:C.text1,
                              marginBottom:8,fontSize:13}}>
                              {isCohort?(d?.cohortName||label):label}
                              {isCohort&&d?.cohortDate&&(
                                <span style={{color:C.text3,fontWeight:400,
                                  marginLeft:6,fontSize:11}}>
                                  · {d.cohortDate}
                                </span>
                              )}
                            </div>
                            <div style={{display:"flex",
                              flexDirection:"column",gap:5}}>
                              <div style={{display:"flex",
                                justifyContent:"space-between",gap:20}}>
                                <span style={{color:C.blue}}>
                                  {isCohort?"👥 Total Enrolled":"📅 Total Joined"}
                                </span>
                                <strong style={{color:C.blue}}>{d?.Joined}</strong>
                              </div>
                              <div style={{display:"flex",
                                justifyContent:"space-between",gap:20}}>
                                <span style={{color:C.red}}>🚪 Resigned</span>
                                <strong style={{color:C.red}}>{d?.Resigned}</strong>
                              </div>
                              <div style={{marginTop:4,paddingTop:6,
                                borderTop:`1px solid ${C.border}`,
                                display:"flex",justifyContent:"space-between",
                                gap:20}}>
                                <span style={{color:C.text3}}>Attrition Rate</span>
                                <strong style={{
                                  color:rate>30?C.red:rate>15?C.amber:C.green,
                                }}>{rate}%</strong>
                              </div>
                            </div>
                          </div>
                        );
                      }}/>
                    <Legend wrapperStyle={{fontSize:12,paddingTop:8}}
                      formatter={value=>(
                        <span style={{
                          color:value==="Joined"?C.blue:C.red,
                          fontWeight:600,
                        }}>
                          {value==="Joined"
                            ?(isCohort?"Enrolled":"Joined")
                            :"Resigned"}
                        </span>
                      )}/>
                    <Bar dataKey="Joined" name="Joined"
                      fill={C.blue} radius={[4,4,0,0]} maxBarSize={28}
                      fillOpacity={0.85}/>
                    <Bar dataKey="Resigned" name="Resigned"
                      fill={C.red} radius={[4,4,0,0]} maxBarSize={28}/>
                  </BarChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div style={{display:"flex",gap:16,marginTop:8,
                  padding:"10px 14px",background:C.bg,borderRadius:8,
                  border:`1px solid ${C.border}`,flexWrap:"wrap",
                  alignItems:"center"}}>
                  <span style={{fontSize:11,fontWeight:700,color:C.text3}}>
                    🔵 Blue = {isCohort?"Total Enrolled":"Total Joined"}
                    &nbsp;·&nbsp;
                    🔴 Red = Resigned
                  </span>
                  <span style={{marginLeft:"auto",display:"flex",
                    gap:14,flexWrap:"wrap"}}>
                    {[
                      {label:"🚨 High Risk",   desc:">30%",   color:C.red},
                      {label:"⚠️ Medium Risk", desc:"15–30%", color:C.amber},
                      {label:"✅ Low Risk",    desc:"<15%",   color:C.green},
                    ].map(l=>(
                      <div key={l.label} style={{display:"flex",
                        alignItems:"center",gap:5,fontSize:11}}>
                        <span style={{width:8,height:8,borderRadius:2,
                          background:l.color,display:"inline-block"}}/>
                        <span style={{color:l.color,fontWeight:700}}>
                          {l.label}
                        </span>
                        <span style={{color:C.text3}}>({l.desc})</span>
                      </div>
                    ))}
                  </span>
                </div>
              </>
            );
          })()}
        
              </div>
            )}

            {chartTab==="scorecard" && (
              <div>
                <div style={{
                  padding:"12px 20px",
                  borderBottom:`1px solid ${C.border}`,
                  display:"flex",justifyContent:"space-between",
                  alignItems:"center",
                }}>
                  <div style={{fontSize:13,fontWeight:600,color:C.text1}}>
                    Division-wise Attrition Scorecard
                  </div>
                  <div style={{fontSize:12,color:C.text3}}>
                    Click column headers to sort
                  </div>
                </div>
                <Table dataSource={filtDivisionRate} columns={divCols}
                  rowKey="division" size="small"
                  pagination={{pageSize:10,showTotal:t=>`${t} divisions`}}
                  rowClassName={r=>r.attritionRate>30?"row-at-risk":""}/>
              </div>
            )}

          </div>{/* end tab content wrapper */}
        </div>{/* end rows 4+5 tabbed panel */}

        {/* ══ ROW 7 — TENURE ANALYSIS ══════════════════════ */}

        <div style={{marginBottom:20}}>

          {/* Section header */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:15,fontWeight:700,color:C.text1,
              display:"flex",alignItems:"center",gap:8}}>
              ⏱️ Tenure-based Attrition Analysis
              <span style={{fontSize:12,fontWeight:400,color:C.text3}}>
                — When do employees tend to resign?
              </span>
            </div>
            <div style={{fontSize:12,color:C.text3,marginTop:3}}>
              Understanding early vs late attrition helps focus retention efforts
            </div>
          </div>

          {/* Bucket colour legend */}
          <div style={{marginBottom:16,padding:'14px 18px',background:C.white,
            borderRadius:12,border:`1px solid ${C.border}`,
            boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
            {/* Row 1: bucket pills */}
            <div style={{display:'flex',gap:8,flexWrap:'wrap',
              alignItems:'center',marginBottom:10}}>
              <span style={{fontSize:11,fontWeight:700,color:C.text3,
                textTransform:'uppercase',letterSpacing:'0.5px',marginRight:4}}>
                Tenure at resignation:
              </span>
              {(stats.tenureBuckets||[]).map(b=>(
                <div key={b} style={{
                  display:'flex',alignItems:'center',gap:5,
                  padding:'4px 12px',borderRadius:20,
                  border:`1.5px solid ${BUCKET_COLORS[b]||C.blue}`,
                  background:`${BUCKET_COLORS[b]||C.blue}15`,
                }}>
                  <span style={{
                    width:8,height:8,borderRadius:'50%',flexShrink:0,
                    background:BUCKET_COLORS[b]||C.blue,
                  }}/>
                  <span style={{
                    fontSize:11,fontWeight:700,
                    color:BUCKET_COLORS[b]||C.blue,
                    whiteSpace:'nowrap',
                  }}>{b}</span>
                </div>
              ))}
            </div>
            {/* Row 2: column key */}
            <div style={{display:'flex',gap:16,flexWrap:'wrap',
              alignItems:'center',paddingTop:8,
              borderTop:`1px solid ${C.border}`}}>
              {[
                {dot:'#3b82f6',label:'Total Enrolled Employees'},
                {dot:'#dc2626',label:'# Resigned (Attrition%)'},
                {dot:C.text3,  label:'CT% = bucket count ÷ total resigned in that row'},
              ].map(({dot,label})=>(
                <div key={label} style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{width:8,height:8,borderRadius:'50%',
                    background:dot,flexShrink:0}}/>
                  <span style={{fontSize:11,color:C.text3}}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Tab bar ── */}
          <div style={{
            display:"flex",background:C.white,
            borderRadius:"12px 12px 0 0",
            border:`1px solid ${C.border}`,
            borderBottom:"none",overflow:"hidden",
          }}>
            {[
              {key:"month",       icon:"📅", label:"Month-wise",  sub:"By resign month"},
              {key:"division",    icon:"🏢", label:"Division",    sub:"By division"},
              {key:"designation", icon:"💼", label:"Designation", sub:"By job role"},
              {key:"cohort",      icon:"👥", label:"Cohort",      sub:"By batch"},
            ].map((t,i,arr)=>{
              const active = tenureTab===t.key;
              return (
                <button key={t.key} onClick={()=>setTenureTab(t.key)} style={{
                  flex:1, padding:"12px 16px", cursor:"pointer",
                  border:"none",
                  borderRight: i<arr.length-1?`1px solid ${C.border}`:"none",
                  borderBottom: active?`3px solid ${C.navy}`:`1px solid ${C.border}`,
                  background: active?C.white:C.surf,
                  textAlign:"left", transition:"all .15s",
                }}>
                  <div style={{fontSize:13,fontWeight:800,
                    color:active?C.navy:C.text2,
                    display:"flex",alignItems:"center",gap:6}}>
                    {t.icon} {t.label}
                  </div>
                  <div style={{fontSize:11,color:C.text3,marginTop:2}}>
                    {t.sub}
                  </div>
                </button>
              );
            })}
          </div>

          {/* ── Tab content ── */}
          <div style={{
            border:`1px solid ${C.border}`,
            borderTop:"none",
            borderRadius:"0 0 14px 14px",
            overflow:"hidden",
          }}>
            {tenureTab==="division" && (
              <TenureTable title="Division × Tenure Attrition"
                icon="🏢" data={filtDivisionTenure}
                rowKey="Division" buckets={stats.tenureBuckets} color={C.navy}
                overrideTotalEmp={decPlusJoined}
                overrideTotalResigned={filteredResignedList.length}/>
            )}
            {tenureTab==="month" && (() => {
              const parseTM = raw => {
                if (!raw) return null;
                const m1 = raw.match(/^(\d{2})-(\w{3})$/);
                if (m1) return dayjs(`20${m1[1]}-${m1[2]}-01`, 'YYYY-MMM-DD');
                const m2 = raw.match(/^(\w{3})[,\s]+(\d{4})$/);
                if (m2) return dayjs(`${m2[2]}-${m2[1]}-01`, 'YYYY-MMM-DD');
                return null;
              };
              const DEC_MS = dayjs('2025-12-01').valueOf();
              const allMT = filtMonthTenure;
              const mainMT = allMT.filter(r => { const d = parseTM(r.month || r.cohort || ''); return !d || d.valueOf() >= DEC_MS; });
              const histMT = allMT.filter(r => { const d = parseTM(r.month || r.cohort || ''); return d && d.valueOf() < DEC_MS; });
              return (
                <>
                  {showHistorical && histMT.length > 0 && (
                    <div style={{ marginBottom: 14, borderRadius: 12, overflow: 'hidden', border: '1.5px dashed #7c3aed' }}>
                      <div style={{ background: '#7c3aed', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>📋 Oct '25 · Nov '25 — Tenure Attrition (Historical)</span>
                        <span style={{ color: '#ddd6fe', fontSize: 11 }}>for reference only</span>
                      </div>
                      <div style={{ background: '#faf5ff' }}>
                        <TenureTable title="" data={histMT} rowKey="Resign Month"
                          buckets={stats.tenureBuckets} color="#7c3aed" monthMode={true} hideHeader={true}/>
                      </div>
                    </div>
                  )}
                  <TenureTable title="Month-wise × Tenure Attrition"
                    icon="📅" data={mainMT}
                    rowKey="Resign Month" buckets={stats.tenureBuckets}
                    color={C.blue} monthMode={true}/>
                </>
              );
            })()}
            {tenureTab==="designation" && (
              <TenureTable title="Designation × Tenure Attrition"
                icon="💼" data={filtDesignationTenure}
                rowKey="Designation" buckets={stats.tenureBuckets} color={C.purple}
                overrideTotalEmp={decPlusJoined}
                overrideTotalResigned={filteredResignedList.length}/>
            )}
            {tenureTab==="cohort" && (
              <TenureTable title="Cohort × Tenure Attrition"
                icon="👥" data={filtCohortTenure}
                rowKey="Cohort" buckets={stats.tenureBuckets} color={C.teal}
                overrideTotalEmp={decPlusJoined}
                overrideTotalResigned={filteredResignedList.length}/>
            )}
          </div>
        </div>

        {/* ══ RESIGNED EMPLOYEE LIST ════════════════════════ */}
        {filteredResignedList.length > 0 && (
          <div style={{
            background:C.white, borderRadius:14,
            border:`1px solid ${C.border}`, overflow:"hidden",
            marginBottom:20,
            boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
          }}>
            {/* Header */}
            <div style={{
              padding:"14px 20px",
              background:`linear-gradient(135deg,${C.navy},#2a4a9f)`,
              display:"flex", justifyContent:"space-between",
              alignItems:"center",
            }}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:"#fff"}}>
                  🚪 Resigned Employees
                </div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",marginTop:2}}>
                  {filteredResignedList.length} employees · sorted by resign date (newest first)
                </div>
              </div>
              <div style={{
                background:"rgba(255,255,255,0.15)",
                border:"1px solid rgba(255,255,255,0.25)",
                borderRadius:20, padding:"4px 14px",
                fontSize:12, fontWeight:700, color:"#fff",
              }}>
                {filteredResignedList.length} total
              </div>
            </div>
{/* ══ RESIGN REASON ANALYTICS ══════════════════════ */}
<div style={{ marginTop: 20 }}>
  <Panel>
    <Sec
      title="Resignation Reason Analysis"
      sub="Unique reasons counted (case-insensitive) · sorted by frequency"
      extra={resignReasonData.length > 0 && (
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <span style={{
            fontSize:12, fontWeight:700, color:C.red,
            background:"#fef2f2", padding:"3px 10px",
            borderRadius:20, border:"1px solid #fecaca",
          }}>
            {resignReasonData.reduce((s,r)=>s+r.value,0)} total resigned
          </span>
          <span style={{
            fontSize:12, fontWeight:600, color:C.text3,
            background:C.surf, padding:"3px 10px",
            borderRadius:20, border:`1px solid ${C.border}`,
          }}>
            {resignReasonData.length} unique reasons
          </span>
        </div>
      )}
    />

    {resignReasonData.length === 0 ? (
      <div style={{ textAlign:"center", paddingTop:40, color:C.text3, fontSize:13 }}>
        No resignation reasons available
      </div>
    ) : (
      <>
        {/* ── Bar chart ── */}
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={resignReasonData}
            margin={{ top: 20, right: 16, left: -10, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
            <XAxis
              dataKey="name" interval={0}
              axisLine={false} tickLine={false} height={75}
              tick={({ x, y, payload }) => (
                <g transform={`translate(${x},${y})`}>
                  <text x={0} y={0} dy={10}
                    textAnchor="end" fill={C.text2}
                    fontSize={11} fontWeight={600}
                    transform="rotate(-38)">
                    {payload.value.length > 20
                      ? payload.value.slice(0,20)+"…"
                      : payload.value}
                  </text>
                </g>
              )}
            />
            <YAxis allowDecimals={false}
              tick={{ fontSize:11, fill:C.text3 }}
              axisLine={false} tickLine={false}/>
            <RTooltip content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0];
              const total = resignReasonData.reduce((s,r)=>s+r.value,0);
              const pct   = total>0 ? Math.round((d.value/total)*100) : 0;
              return (
                <div style={{ background:C.white, border:`1px solid ${C.border}`,
                  borderRadius:10, padding:"10px 14px",
                  boxShadow:"0 4px 12px rgba(0,0,0,0.1)" }}>
                  <div style={{ fontWeight:700, fontSize:13, color:C.text1, marginBottom:6 }}>
                    {d.payload.name}
                  </div>
                  <div style={{ display:"flex", gap:16 }}>
                    <div>
                      <div style={{ fontSize:10, color:C.text3, fontWeight:600, textTransform:"uppercase" }}>Count</div>
                      <div style={{ fontSize:18, fontWeight:800, color:d.fill }}>{d.value}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:10, color:C.text3, fontWeight:600, textTransform:"uppercase" }}>Share</div>
                      <div style={{ fontSize:18, fontWeight:800, color:C.text2 }}>{pct}%</div>
                    </div>
                  </div>
                </div>
              );
            }}/>
            <Bar dataKey="value" name="Employees" radius={[5,5,0,0]} maxBarSize={44}
              label={{ position:"top", fontSize:11, fontWeight:700, fill:C.text2 }}>
              {resignReasonData.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* ── Reason breakdown list ── */}
        {(() => {
          const total = resignReasonData.reduce((s,r)=>s+r.value,0);
          return (
            <div style={{ marginTop:16, display:"flex", flexDirection:"column", gap:7 }}>
              {resignReasonData.map((r,i) => {
                const pct = total>0 ? Math.round((r.value/total)*100) : 0;
                const col = PIE_COLORS[i%PIE_COLORS.length];
                return (
                  <div key={r.name} style={{ display:"flex", alignItems:"center", gap:10 }}>
                    {/* Rank badge */}
                    <div style={{ width:22, height:22, borderRadius:"50%",
                      background:col, color:"#fff",
                      fontSize:10, fontWeight:800,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      flexShrink:0 }}>
                      {i+1}
                    </div>
                    {/* Label */}
                    <span style={{ fontSize:12, fontWeight:600, color:C.text1,
                      flex:1, minWidth:0, overflow:"hidden",
                      textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {r.name}
                    </span>
                    {/* Bar */}
                    <div style={{ flex:2, minWidth:80 }}>
                      <PctBar pct={pct} color={col} h={7}/>
                    </div>
                    {/* Count + pct */}
                    <span style={{ fontSize:12, fontWeight:800, color:col,
                      minWidth:22, textAlign:"right" }}>
                      {r.value}
                    </span>
                    <span style={{ fontSize:11, color:C.text3,
                      minWidth:36, textAlign:"right" }}>
                      {pct}%
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </>
    )}
  </Panel>
</div>
            {/* Table */}
            <Table
              dataSource={filteredResignedList}
              rowKey={r=>`${r.empCode||r.empName}-${r.resignedDate}`}
              size="small"
              pagination={{
                pageSize:10, showSizeChanger:true,
                pageSizeOptions:[10,25,50,100],
                showTotal:t=>(
                  <span style={{color:C.text3,fontSize:12}}>
                    {t} resigned employee{t!==1?"s":""}
                  </span>
                ),
              }}
              scroll={{x:900}}
              columns={[
                { title:"#", width:50, align:"center",
                  render:(_,__,i)=>(
                    <span style={{color:C.text3,fontSize:12,fontWeight:600}}>
                      {i+1}
                    </span>
                  )},
                { title:"Emp Code", dataIndex:"empCode", width:110,
                  sorter:(a,b)=>(a.empCode||"").localeCompare(b.empCode||""),
                  render:v=>v
                    ?<span style={{fontFamily:"monospace",fontWeight:700,
                        color:C.navy,fontSize:12}}>{v}</span>
                    :<span style={{color:C.text3,fontStyle:"italic",fontSize:11}}>
                        null
                      </span>},
                { title:"Employee Name", dataIndex:"empName", width:180,
                  sorter:(a,b)=>(a.empName||"").localeCompare(b.empName||""),
                  render:v=><span style={{fontWeight:600,color:C.text1}}>{v||"—"}</span> },
                { title:"Designation", dataIndex:"designation", width:140,
                  render:v=><span style={{fontSize:12,color:C.text2}}>{v||"—"}</span> },
                { title:"Division", dataIndex:"division", width:110,
                  sorter:(a,b)=>(a.division||"").localeCompare(b.division||""),
                  render:v=><span style={{fontSize:12,color:C.text2}}>{v||"—"}</span> },
                { title:"Join Date", dataIndex:"dateOfJoining", width:105,
                  sorter:(a,b)=>(a.dateOfJoining||"").localeCompare(b.dateOfJoining||""),
                  render:v=>v
                    ?<span style={{fontSize:12,color:C.text2}}>
                        {dayjs(v).format("DD MMM YYYY")}
                      </span>
                    :"—" },
                { title:"Resign Date", dataIndex:"resignedDate", width:105,
                  sorter:(a,b)=>(a.resignedDate||"").localeCompare(b.resignedDate||""),
                  defaultSortOrder:"descend",
                  render:v=>v
                    ?<span style={{
                        fontWeight:700, fontSize:12,
                        color:C.red, background:C.lred,
                        padding:"2px 8px", borderRadius:20,
                      }}>
                        {dayjs(v).format("DD MMM YYYY")}
                      </span>
                    :<span style={{color:C.text3,fontSize:11}}>—</span> },
                { title:"Resign Reason", dataIndex:"resignedReason", width:220,
                  render:v=>{
                    const text = v&&v!=="—"?v:null;
                    return text
                      ?<Tooltip title={text}>
                          <span style={{
                            fontSize:12, color:C.text2,
                            cursor:"help",
                            display:"block",
                            maxWidth:200,
                            overflow:"hidden",
                            textOverflow:"ellipsis",
                            whiteSpace:"nowrap",
                          }}>
                            {text}
                          </span>
                        </Tooltip>
                      :<span style={{color:C.text3,fontSize:11,
                          fontStyle:"italic"}}>Not provided</span>;
                  }},
              ]}
            />
          </div>
        )}


        {/* ══ EC STATUS TABLE ══════════════════════════════════ */}
        {(() => {
          const resignedList = stats.resignedList || [];
          const allList      = stats.allEmployees || resignedList; // use all if available
          if (!resignedList.length) return null;

          // Exclude CCD/Venus (join>Dec'25 + resign>=May'26), After-180 days, Y-code
          const CLOSED_DIVS_SET   = new Set(['ccd', 'venus']);
          const MAY_2026_MATRIX   = dayjs('2026-05-01').valueOf();
          const DEC_2025_MATRIX   = dayjs('2025-12-01').valueOf();
          const resignedListForMatrix = resignedList.filter(e => {
            const div = (e.division || '').trim().toLowerCase();
            const doj = e.dateOfJoining || e.cohortDate;
            if (CLOSED_DIVS_SET.has(div) && doj && dayjs(doj).valueOf() > DEC_2025_MATRIX &&
                e.resignedDate && dayjs(e.resignedDate).valueOf() >= MAY_2026_MATRIX) return false;
            if (doj && e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return false;
            if ((e.empCode || '').toUpperCase().startsWith('Y')) return false;
            return true;
          });

          // ── All unique resign months sorted chronologically ────────────
          const monthSet = new Set();
          resignedListForMatrix.forEach(e => {
            if (e.resignedDate) monthSet.add(dayjs(e.resignedDate).format('MMM YYYY'));
          });
          const resignMonths = [...monthSet].sort((a, b) =>
            dayjs(a, 'MMM YYYY').valueOf() - dayjs(b, 'MMM YYYY').valueOf()
          );
          if (!resignMonths.length) return null;

          // ── Build join-month rows directly from resignedList ──────────
          // Use employee's dateOfJoining to determine their join month.
          // This avoids fragile cohort-key string matching entirely.

          // Step 1: collect all join months that appear in cohortTenure
          // so we know the totalJoined per join month (from enrolled data)
          const cohortTenure = stats.cohortTenure || [];
          const joinedPerMonth = {};   // 'MMM YYYY' → total enrolled count
          const cohortCountPerMonth = {};

          cohortTenure.filter(ct => ct.totalEmployees > 0).forEach(ct => {
            const parts    = ct.cohort?.split(' · ') || [];
            const rawDate  = parts[1] || '';
            const jm = rawDate ? dayjs(rawDate, 'DD MMM YY').format('MMM YYYY') : null;
            if (!jm) return;
            joinedPerMonth[jm]    = (joinedPerMonth[jm]    || 0) + ct.totalEmployees;
            cohortCountPerMonth[jm] = (cohortCountPerMonth[jm] || 0) + 1;
          });

          // Step 2: for each resigned employee, bucket them by their
          // dateOfJoining month AND by resignedDate month
          const perMonthMap = {};  // joinMonth → { resignMonth → count }

          resignedListForMatrix.forEach(e => {
            if (!e.resignedDate) return;
            // Use dateOfJoining if available, else cohortDate, else skip
            const doj = e.dateOfJoining || e.cohortDate;
            if (!doj) return;
            // Employees who completed their 180-day journey — not counted as EC attrition
            if (dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return;
            const jm = dayjs(doj).format('MMM YYYY');
            const rm = dayjs(e.resignedDate).format('MMM YYYY');
            if (!perMonthMap[jm]) {
              perMonthMap[jm] = {};
              resignMonths.forEach(m => { perMonthMap[jm][m] = 0; });
            }
            if (perMonthMap[jm][rm] !== undefined) perMonthMap[jm][rm]++;
          });

          // Extra exclusion counts per join month (shown as informational summary columns)
          const after180ByJoinMonth  = {};  // resigned >= 180 days after joining
          const ccdVenusByJoinMonth  = {};  // CCD/Venus resigned May 2026+
          const yCodeByJoinMonth     = {};  // emp_code starting with Y
          resignedList.forEach(e => {
            const doj = e.dateOfJoining || e.cohortDate;
            if (!doj || !e.resignedDate) return;
            const jm  = dayjs(doj).format('MMM YYYY');
            if (dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) {
              after180ByJoinMonth[jm] = (after180ByJoinMonth[jm] || 0) + 1;
            }
            const div = (e.division || '').trim().toLowerCase();
            if (CLOSED_DIVS_SET.has(div) && dayjs(doj).valueOf() > DEC_2025_MATRIX &&
                dayjs(e.resignedDate).valueOf() >= MAY_2026_MATRIX) {
              ccdVenusByJoinMonth[jm] = (ccdVenusByJoinMonth[jm] || 0) + 1;
            }
            if ((e.empCode || '').toUpperCase().startsWith('Y')) {
              yCodeByJoinMonth[jm] = (yCodeByJoinMonth[jm] || 0) + 1;
            }
          });

          // Step 3: merge into rows — union of joinedPerMonth keys + perMonthMap keys
          const allJoinMonths = new Set([
            ...Object.keys(joinedPerMonth),
            ...Object.keys(perMonthMap),
          ]);

          const monthRows = [...allJoinMonths]
            .sort((a, b) => dayjs(a, 'MMM YYYY').valueOf() - dayjs(b, 'MMM YYYY').valueOf())
            .map(jm => {
              const totalJoined      = joinedPerMonth[jm]    || 0;
              const cohortCount      = cohortCountPerMonth[jm] || 0;
              const perResign        = perMonthMap[jm] || Object.fromEntries(resignMonths.map(m=>[m,0]));
              const totalResigned    = Object.values(perResign).reduce((s,v)=>s+v, 0);
              const after180Resigned = after180ByJoinMonth[jm]  || 0;
              const ccdVenusResigned = ccdVenusByJoinMonth[jm]  || 0;
              const yCodeResigned    = yCodeByJoinMonth[jm]     || 0;
              const active           = totalJoined - totalResigned - after180Resigned - ccdVenusResigned - yCodeResigned;
              const lossPct          = totalJoined > 0 ? Math.round((totalResigned / totalJoined) * 100) : 0;
              const retainPct        = totalJoined > 0 ? Math.round((active / totalJoined) * 100) : 0;
              const label            = dayjs(jm, 'MMM YYYY').format("MMM'YY");
              return { jm, label, totalJoined, totalResigned, active, lossPct, retainPct, perResign, cohortCount, after180Resigned, ccdVenusResigned, yCodeResigned };
            });

          if (!monthRows.length) return null;

          // ── Only show Dec'25 onwards in the matrix rows ───────────────
          // Employees who joined Oct'25 / Nov'25 have completed their 180-day journey;
          // exclude them from the matrix view but keep them in the TOTAL counts.
          const DEC_2025 = dayjs('2025-12-01').valueOf();
          const visibleRows = monthRows.filter(
            r => dayjs(r.jm, 'MMM YYYY').valueOf() >= DEC_2025
          );
          const preDec25Rows = monthRows.filter(
            r => dayjs(r.jm, 'MMM YYYY').valueOf() < DEC_2025
          );
          // Historical (Oct'25 + Nov'25): use full resignedList for actual attrition counts
          const histResignedPerMonth = {};
          resignedList.forEach(e => {
            const doj = e.dateOfJoining || e.cohortDate;
            if (!doj || dayjs(doj).valueOf() >= DEC_2025) return;
            const jm = dayjs(doj).format('MMM YYYY');
            histResignedPerMonth[jm] = (histResignedPerMonth[jm] || 0) + 1;
          });
          const histJoined   = preDec25Rows.reduce((s, r) => s + r.totalJoined, 0);
          const histResigned = Object.values(histResignedPerMonth).reduce((s, v) => s + v, 0);
          const histActive   = histJoined - histResigned;
          const histAttrPct  = histJoined > 0 ? Math.round((histResigned / histJoined) * 100) : 0;
          // Resign-month columns: Dec'25+ joiners cannot have resigned before Dec'25,
          // so Oct'25 / Nov'25 resign columns would be all zeros — hide them.
          const visibleResignMonths = resignMonths.filter(
            m => dayjs(m, 'MMM YYYY').valueOf() >= DEC_2025
          );

          // ── TOTAL row ─────────────────────────────────────────────────
          const totJoined   = monthRows.reduce((s, r) => s + r.totalJoined,   0);
          const totResigned = monthRows.reduce((s, r) => s + r.totalResigned, 0);
          const totActive   = totJoined - totResigned;

          // Use authoritative counts from API so all KPI numbers match the DB exactly.
          // totJoined/totResigned from matrix rows can be lower because:
          //   • employees without dateOfJoining are excluded from matrix rows (963 - 957 = 6)
          //   • unlinked attrition records have no matching employee (166 - 165 = 1)
          const displayTotal    = stats?.total        ?? totJoined;
          const displayResigned = stats?.totalResigned ?? totResigned;
          const displayActive   = displayTotal - displayResigned;
          const displayAttrPct  = displayTotal > 0 ? Math.round((displayResigned / displayTotal) * 100) : 0;

          // EC-specific totals — Dec'25 onwards only (visible rows)
          const ecTotal       = visibleRows.reduce((s, r) => s + r.totalJoined,       0);
          const ecResigned    = visibleRows.reduce((s, r) => s + r.totalResigned,     0);
          const ecAfter180    = visibleRows.reduce((s, r) => s + r.after180Resigned,  0);
          const ecCcdVenus    = visibleRows.reduce((s, r) => s + r.ccdVenusResigned,  0);
          const ecYCode       = visibleRows.reduce((s, r) => s + r.yCodeResigned,     0);
          const ecTrueActive  = ecTotal - ecResigned - ecAfter180 - ecCcdVenus - ecYCode;
          const ecActive      = ecTotal - ecResigned;
          const ecAttrPct     = ecTotal > 0 ? Math.round((ecResigned / ecTotal) * 100) : 0;
          const totPerMonth = {};
          visibleResignMonths.forEach(m => {
            totPerMonth[m] = visibleRows.reduce((s, r) => s + (r.perResign[m] || 0), 0);
          });

          // ── Under-10-days per resign month (Dec'25+ joiners only) ────
          const under10 = {};
          visibleResignMonths.forEach(m => { under10[m] = 0; });
          resignedListForMatrix.forEach(e => {
            if (!e.resignedDate || !e.dateOfJoining) return;
            // Skip Oct'25 / Nov'25 joiners — not shown in this matrix view
            if (dayjs(e.dateOfJoining).valueOf() < DEC_2025) return;
            const days = dayjs(e.resignedDate).diff(dayjs(e.dateOfJoining), 'day');
            if (days <= 10) {
              const m = dayjs(e.resignedDate).format('MMM YYYY');
              if (under10[m] !== undefined) under10[m]++;
            }
          });
          const totUnder10 = Object.values(under10).reduce((s, v) => s + v, 0);
          const visibleResigned = visibleRows.reduce((s, r) => s + r.totalResigned, 0);
          const under10Pct = visibleResigned > 0 ? Math.round((totUnder10 / visibleResigned) * 100) : 0;

          // ── Styles ────────────────────────────────────────────────────
          const TH = (extra = {}) => ({
            padding: '9px 10px', textAlign: 'center', fontSize: 11,
            fontWeight: 700, color: '#fff',
            borderRight: '1px solid rgba(255,255,255,0.15)',
            whiteSpace: 'nowrap', ...extra,
          });
          const TD = (bg = 'transparent') => ({
            padding: '8px 10px', textAlign: 'center', fontSize: 12,
            fontWeight: 600, borderRight: `1px solid ${C.border}`,
            background: bg, whiteSpace: 'nowrap',
          });

          return (
            <div style={{ marginBottom: 24 }}>

              {/* ── Section header ── */}
              <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text1,
                    display: 'flex', alignItems: 'center', gap: 8 }}>
                    📊 EC Status — Join Month × Resign Month
                    <span style={{ fontSize: 12, fontWeight: 400, color: C.text3 }}>
                      — All cohorts in same month are merged into one row
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: C.text3, marginTop: 3 }}>
                    Each row = total employees who joined that month · columns = which month they resigned
                  </div>
                </div>
                <button
                  onClick={() => setShowHistorical(v => !v)}
                  title="View Oct '25 · Nov '25 historical cohort data"
                  style={{
                    padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                    border: `1.5px solid ${showHistorical ? '#7c3aed' : '#c4b5fd'}`,
                    background: showHistorical ? '#7c3aed' : '#f5f3ff',
                    color: showHistorical ? '#fff' : '#7c3aed',
                    whiteSpace: 'nowrap',
                  }}
                >
                  📋 {showHistorical ? 'Hide' : 'Show'} Oct '25 · Nov '25
                </button>
              </div>

              {/* Oct'25 · Nov'25 historical cohort panel */}
              {showHistorical && preDec25Rows.length > 0 && (
                <div style={{ marginBottom: 14, borderRadius: 12, overflow: 'hidden',
                  border: '1.5px dashed #7c3aed' }}>
                  <div style={{ background: '#7c3aed', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>📋 Oct '25 · Nov '25 — Historical Cohort</span>
                    <span style={{ color: '#ddd6fe', fontSize: 11 }}>180-day journey completed · for reference only</span>
                  </div>
                  <div style={{ background: '#faf5ff', padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                      {[
                        { icon: '👥', label: 'Joined',      val: histJoined,        color: C.blue,  bg: '#eff6ff' },
                        { icon: '🚪', label: 'Resigned',    val: histResigned,      color: C.red,   bg: '#fef2f2' },
                        { icon: '✅', label: 'Active',      val: histActive,        color: C.green, bg: '#f0fdf4' },
                        { icon: '📊', label: 'Attrition %', val: histAttrPct + '%', color: C.amber, bg: '#fffbeb' },
                      ].map(k => (
                        <div key={k.label} style={{ padding: '8px 12px', borderRadius: 8, flex: 1, minWidth: 90,
                          background: k.bg, border: `1px solid ${k.color}33` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ fontSize: 16 }}>{k.icon}</span>
                            <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.val}</div>
                          </div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: C.text3, textTransform: 'uppercase', marginTop: 3 }}>{k.label}</div>
                        </div>
                      ))}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#7c3aed' }}>
                          {['Join Month', 'Joined', 'Resigned', 'Active', '% Loss'].map(h => (
                            <th key={h} style={{ padding: '6px 10px', color: '#fff', fontWeight: 700,
                              textAlign: h === 'Join Month' ? 'left' : 'center',
                              borderRight: '1px solid rgba(255,255,255,0.2)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preDec25Rows.map((row, i) => {
                          const res = histResignedPerMonth[row.jm] || 0;
                          const act = row.totalJoined - res;
                          const lp  = row.totalJoined > 0 ? Math.round((res / row.totalJoined) * 100) : 0;
                          return (
                            <tr key={row.jm} style={{ background: i % 2 === 0 ? '#f5f3ff' : '#fff',
                              borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '7px 10px', fontWeight: 700, color: '#7c3aed' }}>{row.label}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 700, color: C.blue }}>{row.totalJoined}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 700, color: C.red }}>{res}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 700, color: C.green }}>{act}</td>
                              <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 700,
                                color: lp > 20 ? C.red : lp > 10 ? C.amber : C.green }}>{lp}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* EC summary strip (Dec'25+ only) */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                {[
                  { icon: '👥', val: ecTotal,           label: 'EC Total Joined',   color: C.blue,    bg: '#eff6ff' },
                  { icon: '🚪', val: ecResigned,        label: 'EC Total Resigned', color: C.red,     bg: '#fef2f2' },
                  { icon: '✅', val: decPlusInJourney,  label: 'EC Total Active',   color: C.green,   bg: '#f0fdf4' },
                  { icon: '📊', val: ecAttrPct + '%',   label: 'EC Attrition %',   color: C.amber,   bg: '#fffbeb' },
                  ...(showInfoCols ? [
                    { icon: '⏳', val: ecAfter180,       label: 'After 180',         color: '#7c3aed', bg: '#f5f3ff' },
                    { icon: '🏢', val: ecCcdVenus,       label: 'CCD / Venus',       color: '#ea580c', bg: '#fff7ed' },
                    { icon: '🏷️', val: ecYCode,          label: 'Y Code',            color: '#059669', bg: '#ecfdf5' },
                  ] : []),
                ].map(k => (
                  <div key={k.label} style={{
                    padding: '10px 14px', borderRadius: 10, flex: 1, minWidth: 95,
                    background: k.bg, border: `1px solid ${k.color}33`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 18, lineHeight: 1 }}>{k.icon}</span>
                      <div style={{ fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1 }}>
                        {k.val}
                      </div>
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.text3,
                      textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 5 }}>
                      {k.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Main table ── */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <button
                  onClick={() => setShowInfoCols(v => !v)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', border: `1.5px solid ${showInfoCols ? '#7c3aed' : C.border}`,
                    background: showInfoCols ? '#f5f3ff' : C.white,
                    color: showInfoCols ? '#7c3aed' : C.text3,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {showInfoCols ? '▲ Hide' : '▼ Show'} After 180 / CCD/Venus / Y Code
                </button>
              </div>
              <div style={{
                background: C.white, borderRadius: 14,
                border: `1px solid ${C.border}`, overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>

                    {/* ── HEADER ── */}
                    <thead>
                      <tr style={{ background: '#0f2352' }}>
                        <th colSpan={2} style={{ ...TH({ textAlign: 'left', borderRight: '1px solid rgba(255,255,255,0.25)' }) }}>
                          Join Month
                        </th>
                        <th colSpan={resignMonths.length} style={{ ...TH({ borderRight: '1px solid rgba(255,255,255,0.25)', color: '#fca5a5' }) }}>
                          ← Resigned in Month →
                        </th>
                        <th colSpan={showInfoCols ? 7 : 4} style={{ ...TH({ borderRight: 'none', color: '#86efac' }) }}>
                          Summary
                        </th>
                      </tr>
                      <tr style={{ background: C.navy }}>
                        <th style={{ ...TH({ textAlign: 'left', minWidth: 100, position: 'sticky', left: 0, background: C.navy }) }}>
                          Month
                        </th>
                        <th style={{ ...TH({ minWidth: 70, color: '#93c5fd' }) }}>
                          Joined
                        </th>
                        {visibleResignMonths.map(m => (
                          <th key={m} style={{ ...TH({ minWidth: 64, color: '#fca5a5' }) }}>
                            {dayjs(m, 'MMM YYYY').format('MMM')}<br/>
                            <span style={{ fontSize: 10, opacity: 0.75, fontWeight: 500 }}>
                              {dayjs(m, 'MMM YYYY').format('YY')}
                            </span>
                          </th>
                        ))}
                        <th style={{ ...TH({ minWidth: 72, color: '#fca5a5' }) }}>Resigned</th>
                        <th style={{ ...TH({ minWidth: 70, color: '#86efac' }) }}>Active</th>
                        <th style={{ ...TH({ minWidth: 68, color: '#fde68a' }) }}>% Loss</th>
                        <th style={{ ...TH({ minWidth: 68, color: '#86efac', ...(!showInfoCols && { borderRight: 'none' }) }) }}>% Active</th>
                        {showInfoCols && <>
                          <th style={{ ...TH({ minWidth: 80, color: '#c4b5fd' }) }}>After 180</th>
                          <th style={{ ...TH({ minWidth: 80, color: '#fdba74' }) }}>CCD/Venus</th>
                          <th style={{ ...TH({ minWidth: 80, color: '#6ee7b7', borderRight: 'none' }) }}>Y Code</th>
                        </>}
                      </tr>
                    </thead>

                    <tbody>
                      {visibleRows.map((row, i) => (
                        <tr key={row.jm} style={{
                          borderBottom: `1px solid ${C.border}`,
                          background: i % 2 === 0 ? C.white : '#fafcff',
                        }}>
                          {/* Join month label */}
                          <td style={{
                            padding: '9px 12px', fontSize: 12, fontWeight: 700,
                            color: C.navy, position: 'sticky', left: 0,
                            background: i % 2 === 0 ? C.white : '#fafcff',
                            borderRight: `1px solid ${C.border}`,
                          }}>
                            {row.label}
                            {row.cohortCount > 1 && (
                              <span style={{
                                marginLeft: 6, fontSize: 10, fontWeight: 600,
                                color: C.blue, background: '#eff6ff',
                                padding: '1px 6px', borderRadius: 10,
                                border: '1px solid #bfdbfe',
                              }}>
                                {row.cohortCount} cohorts
                              </span>
                            )}
                          </td>

                          {/* Joined */}
                          <td style={TD('#eff6ff')}>
                            <span style={{ fontWeight: 800, color: C.blue }}>{row.totalJoined}</span>
                          </td>

                          {/* Resigned per resign-month */}
                          {visibleResignMonths.map(m => {
                            const cnt        = row.perResign[m] || 0;
                            const isOwnMonth = m === row.jm;
                            const bg = isOwnMonth ? '#FEF9C3'
                                     : cnt > 0    ? '#fef2f2' : 'transparent';
                            return (
                              <td key={m} style={TD(bg)}>
                                {cnt > 0
                                  ? <span style={{ fontWeight: 800,
                                      color: isOwnMonth ? C.amber : C.red }}>
                                      {cnt}
                                    </span>
                                  : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>
                                }
                              </td>
                            );
                          })}

                          {/* Total resigned */}
                          <td style={TD('#fef2f2')}>
                            {row.totalResigned > 0
                              ? <span style={{ fontWeight: 800, color: C.red }}>{row.totalResigned}</span>
                              : <span style={{ color: '#d1d5db' }}>0</span>}
                          </td>

                          {/* Active */}
                          <td style={TD('#f0fdf4')}>
                            <span style={{ fontWeight: 800, color: C.green }}>{row.active}</span>
                          </td>

                          {/* % Loss */}
                          <td style={TD()}>
                            <span style={{ fontWeight: 800,
                              color: row.lossPct > 20 ? C.red : row.lossPct > 10 ? C.amber : C.green }}>
                              {row.lossPct}%
                            </span>
                          </td>

                          {/* % Retain */}
                          <td style={{ ...TD(), ...(!showInfoCols && { borderRight: 'none' }) }}>
                            <span style={{ fontWeight: 800,
                              color: row.retainPct >= 90 ? C.green : row.retainPct >= 80 ? C.amber : C.red }}>
                              {row.retainPct}%
                            </span>
                          </td>

                          {showInfoCols && <>
                            {/* After 180 resigned */}
                            <td style={TD('#f5f3ff')}>
                              {row.after180Resigned > 0
                                ? <span style={{ fontWeight: 800, color: '#7c3aed' }}>{row.after180Resigned}</span>
                                : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>}
                            </td>

                            {/* CCD/Venus resigned May 2026+ */}
                            <td style={TD('#fff7ed')}>
                              {row.ccdVenusResigned > 0
                                ? <span style={{ fontWeight: 800, color: '#ea580c' }}>{row.ccdVenusResigned}</span>
                                : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>}
                            </td>

                            {/* Y Code */}
                            <td style={{ ...TD('#ecfdf5'), borderRight: 'none' }}>
                              {row.yCodeResigned > 0
                                ? <span style={{ fontWeight: 800, color: '#059669' }}>{row.yCodeResigned}</span>
                                : <span style={{ color: '#e5e7eb', fontSize: 11 }}>—</span>}
                            </td>
                          </>}
                        </tr>
                      ))}

                      {/* ── TOTAL ROW ── */}
                      <tr style={{ background: C.navy, borderTop: `2px solid ${C.navy}` }}>
                        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 800,
                          color: '#fff', position: 'sticky', left: 0, background: C.navy,
                          borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                          TOTAL
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#93c5fd', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                          {ecTotal}
                        </td>
                        {visibleResignMonths.map(m => (
                          <td key={m} style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 13, color: totPerMonth[m] > 0 ? '#fca5a5' : 'rgba(255,255,255,0.3)',
                            borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                            {totPerMonth[m] || 0}
                          </td>
                        ))}
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#fca5a5', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                          {ecResigned}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#86efac', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                          {decPlusInJourney}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#fde68a', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                          {ecAttrPct}%
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                          fontSize: 14, color: '#86efac', ...(!showInfoCols && { borderRight: 'none' }) }}>
                          {ecTotal > 0 ? Math.round((decPlusInJourney / ecTotal) * 100) : 0}%
                        </td>
                        {showInfoCols && <>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#c4b5fd', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                            {ecAfter180}
                          </td>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#fdba74', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                            {ecCcdVenus}
                          </td>
                          <td style={{ padding: '10px', textAlign: 'center', fontWeight: 800,
                            fontSize: 14, color: '#6ee7b7' }}>
                            {ecYCode}
                          </td>
                        </>}
                      </tr>
                    </tbody>

                    {/* ── UNDER 10 DAYS FOOTER ── */}
                    <tfoot>
                      <tr style={{ background: '#fff7ed', borderTop: `2px solid ${C.amber}55` }}>
                        <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700,
                          color: C.amber, position: 'sticky', left: 0, background: '#fff7ed',
                          borderRight: `1px solid ${C.border}` }}>
                          ⚡ Resigned &lt; 10 days
                        </td>
                        <td style={{ padding: '8px 10px', background: '#fff7ed',
                          borderRight: `1px solid ${C.border}` }} />
                        {visibleResignMonths.map(m => (
                          <td key={m} style={{ padding: '8px 10px', textAlign: 'center',
                            fontSize: 12, fontWeight: 700, background: '#fff7ed',
                            borderRight: `1px solid ${C.border}` }}>
                            {under10[m] > 0
                              ? <span style={{ fontWeight: 800, color: C.red }}>{under10[m]}</span>
                              : <span style={{ color: '#d1d5db' }}>—</span>}
                          </td>
                        ))}
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 800,
                          color: C.red, background: '#fff7ed', borderRight: `1px solid ${C.border}` }}>
                          {totUnder10}
                        </td>
                        <td style={{ padding: '8px 10px', background: '#fff7ed',
                          borderRight: `1px solid ${C.border}` }} />
                        <td style={{ padding: '8px 10px', background: '#fff7ed',
                          borderRight: `1px solid ${C.border}` }} />
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 800,
                          background: '#fff7ed', borderRight: showInfoCols ? `1px solid ${C.border}` : 'none',
                          color: under10Pct > 30 ? C.red : under10Pct > 15 ? C.amber : C.green }}>
                          {under10Pct}%
                        </td>
                        {showInfoCols && <>
                          <td style={{ padding: '8px 10px', background: '#fff7ed',
                            borderRight: `1px solid ${C.border}` }} />
                          <td style={{ padding: '8px 10px', background: '#fff7ed',
                            borderRight: `1px solid ${C.border}` }} />
                          <td style={{ padding: '8px 10px', background: '#fff7ed', borderRight: 'none' }} />
                        </>}
                      </tr>
                    </tfoot>

                  </table>
                </div>

                {/* ── Legend ── */}
                <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}`,
                  background: C.bg, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                  {[
                    { color: '#FEF9C3', border: C.amber, label: 'Resigned in own join month' },
                    { color: '#fef2f2', border: C.red,   label: 'Resigned in later month' },
                    { color: '#fff7ed', border: C.amber, label: '⚡ Under 10 days — critical early exit' },
                  ].map(({ color, border, label }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3,
                        background: color, border: `1.5px solid ${border}`, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: C.text3 }}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ══ ALL CLEAR BANNER ══════════════════════════════ */}
        {decPlusResigned===0 && (
          <div style={{
            background:"linear-gradient(135deg,#dcfce7 0%,#bbf7d0 100%)",
            border:`1.5px solid ${C.green}44`,borderRadius:14,
            padding:"22px 28px",marginTop:24,
            display:"flex",alignItems:"center",gap:16,
            boxShadow:"0 2px 12px rgba(22,163,74,0.15)",
          }}>
            <span style={{fontSize:44}}>🏆</span>
            <div>
              <div style={{fontWeight:800,fontSize:18,color:C.green}}>
                Zero Attrition — Outstanding Achievement!
              </div>
              <div style={{fontSize:13,color:"#166534",marginTop:4}}>
                No employees have resigned. This reflects excellent engagement,
                culture, and leadership across all divisions. Keep it up! 💪
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ══ 180-DAY COMPLETED JOURNEY MODAL ════════════════════ */}
      <Modal
        open={show180Modal}
        onCancel={() => { setShow180Modal(false); setModal180Search(''); }}
        footer={null}
        width={860}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🎓</span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#7c3aed' }}>
                Completed 180-Day Journey
              </div>
              <div style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', marginTop: 1 }}>
                Dec '25 onwards · active employees with 180+ days tenure
              </div>
            </div>
            <div style={{ marginLeft: 'auto', background: '#f5f3ff',
              border: '1.5px solid #7c3aed', borderRadius: 8,
              padding: '4px 12px', fontSize: 22, fontWeight: 900, color: '#7c3aed' }}>
              {completed180List.length}
            </div>
          </div>
        }
      >
        {/* Search bar */}
        <Input.Search
          placeholder="Search by Emp Code, Name, or Date of Joining (e.g. 03 Dec 2025)"
          allowClear
          value={modal180Search}
          onChange={e => setModal180Search(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        {(() => {
          const q = modal180Search.trim().toLowerCase();
          const filtered180 = q
            ? completed180List.filter(e => {
                const doj = e.dateOfJoining || e.cohortDate;
                return (
                  (e.empCode  || '').toLowerCase().includes(q) ||
                  (e.empName  || '').toLowerCase().includes(q) ||
                  (doj ? dayjs(doj).format('DD MMM YYYY').toLowerCase().includes(q) : false)
                );
              })
            : completed180List;
          return (
            <Table
              size="small"
              dataSource={filtered180}
              rowKey={r => r.empCode || r.empName}
              pagination={{ pageSize: 10, showSizeChanger: false,
                showTotal: (t, [s, e]) => `${s}–${e} of ${t} employees` }}
              columns={[
                { title: 'Emp Code', dataIndex: 'empCode', width: 90,
                  render: v => <span style={{ fontWeight: 700, color: '#7c3aed', fontSize: 12 }}>{v||'—'}</span> },
                { title: 'Name', dataIndex: 'empName', ellipsis: true,
                  render: v => <span style={{ fontWeight: 600, fontSize: 13 }}>{v||'—'}</span> },
                { title: 'Division', dataIndex: 'division', width: 120,
                  render: v => v ? <Tag style={{ borderRadius: 6, fontSize: 11 }}>{v}</Tag> : '—' },
                { title: 'Date of Joining', dataIndex: 'dateOfJoining', width: 130,
                  render: v => v ? dayjs(v).format('DD MMM YYYY') : '—',
                  sorter: (a, b) => dayjs(a.dateOfJoining||a.cohortDate).valueOf() - dayjs(b.dateOfJoining||b.cohortDate).valueOf(),
                  defaultSortOrder: 'ascend' },
                { title: 'Days Completed', width: 130, align: 'center',
                  render: (_, r) => {
                    const doj = r.dateOfJoining || r.cohortDate;
                    const days = doj ? dayjs().diff(dayjs(doj), 'day') : null;
                    return days !== null
                      ? <Tag color="purple" style={{ borderRadius: 6, fontWeight: 700, fontSize: 12 }}>{days}d</Tag>
                      : '—';
                  },
                  sorter: (a, b) => {
                    const da = dayjs().diff(dayjs(a.dateOfJoining||a.cohortDate), 'day');
                    const db = dayjs().diff(dayjs(b.dateOfJoining||b.cohortDate), 'day');
                    return da - db;
                  }},
                { title: 'Status', width: 100, align: 'center',
                  render: () => <Tag color="success" style={{ borderRadius: 6, fontWeight: 700 }}>✅ Active</Tag> },
              ]}
              style={{ marginTop: 0 }}
            />
          );
        })()}
      </Modal>

      <style>{`
        .row-at-risk > td { background:#fff9f9 !important; }
        .row-at-risk:hover > td { background:#fff0f0 !important; }
        .ant-table-thead > tr > th {
          background:#f8fafc !important; color:#4a6580 !important;
          font-size:11px !important; font-weight:700 !important;
          text-transform:uppercase !important; letter-spacing:0.5px !important;
          border-bottom:1px solid #dce6f0 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom:1px solid #dce6f080 !important;
          padding:10px 14px !important;
        }
        .ant-table-tbody > tr:hover > td { background:#f8faff !important; }
        .ant-card-head { border-bottom:none !important; }
      `}</style>
    </div>
  );
}